/**
 * Rough Trade–style discover panel on search focus.
 * Empty query: personalized Best Sellers. Typing: live fuzzy-ranked catalog search.
 * Graph-race remount for typed intent (v2026-08-04b).
 * Nuclear clear remount + explore-with-nodes (v20260807e) — CDN bust must change body.
 */
(function () {
  var root = null;
  var grid = null;
  var emptyEl = null;
  var titleEl = null;
  var whyEl = null;
  var panelEl = null;
  var data = null;
  var open = false;
  var bound = false;
  var openedAt = 0;
  var suppressNextOutsideClose = false;
  var CLOSE_IGNORE_MS = 400;
  var DEBOUNCE_MS = 40;
  /** Typed settle debounce — short so lean/committed paint-on-settle feels instant. */
  /** Debounce typed preference signal — plan: 300ms so fleeting keys don't dominate. */
  var SEARCH_SIGNAL_MS = 300;
  var GRID_LIMIT = 32;
  var CACHE_MAX = 48;
  var debounceTimer = null;
  var searchSignalTimer = null;
  var searchSignalIdleGen = 0;
  var lastSearchSignal = '';
  var pendingSearchSignalQuery = '';
  var lastResultsForSignal = [];
  var lastFlushedQuery = '';
  var hoverTimer = null;
  var hoverHandle = null;
  var hoverStartedAt = 0;
  var visibleLogged = null; // Set of handles logged this open
  var visibilityObserver = null;
  // Results-session noise gates
  var resultsShownAt = 0;
  var resultsEngaged = false;
  var cardSeenAt = {};
  var cardEngaged = {};
  var scrollPastDone = {};
  var BOUNCE_CLEAR_MS = 800;
  var SCROLL_PAST_MS = 200;
  var abortCtrl = null;
  var requestSeq = 0;
  var mode = 'bestsellers'; // bestsellers | results
  var resultCache = {}; // normalized query -> mapped product array
  var cacheOrder = [];
  var lastPaintKey = '';
  /** Clears suppressEmpty blank if similar/Shopify never settles. */
  var suppressEmptyTimer = null;
  var suppressEmptySeq = 0;
  var rankedCache = null; // memoized For-you list (rankByAffinity / field rank)
  var rankedCacheAt = 0;
  /** True when rankedCache was primed on settle — clear path paints without re-rank. */
  var settleRankPrimed = false;
  var lastBestsellersPaint = null; // last painted bestsellers for instant local search
  /* Search chrome only — never the whole .wmn-header-shell / .sticky_nav
     (those stole clicks from .wmn-header__nav-link). */
  var SEARCH_SAFE =
    '.wmn-header__search, .header_search_form, [data-dropdown="search"], .icon-search, .wmn-search-clear, a[data-dropdown-rel="search"]';

  var DISPLAY_LIMIT = 32;
  var RANK_CACHE_MS = 60000;
  // First desktop row is 8 cols; eager-flooding all 32 saturates CDN.
  // SSR liquid already ships the first row — keep that budget on remounts.
  // Mobile/tablet: fewer columns → fewer eager downloads.
  function eagerCoverBudget() {
    var w = window.innerWidth || 1280;
    if (w <= 640) return 6;
    if (w <= 1024) return 8;
    return 12;
  }
  var EAGER_COVER_N = eagerCoverBudget();
  /** Panel-rooted IO — native loading=lazy still floods all rows (Chrome preload margin). */
  var coverSrcObserver = null;
  /** Coalesce pointerdown+focusin show() so layout work can't stack before first paint. */
  var warmScheduled = false;
  var pendingShowInput = null;
  var lastPointerShowAt = 0;
  /** Bumped to cancel deferred seed-expand timeouts (must not overwrite ranked For you). */
  var seedExpandGen = 0;

  /** First-row cover imgs currently in the grid (SSR or painted). */
  function firstRowCoverImgs() {
    if (!grid) return [];
    EAGER_COVER_N = eagerCoverBudget();
    var imgs = grid.querySelectorAll('.wmn-search-discover__cover img[src]');
    var out = [];
    var n = Math.min(imgs.length, EAGER_COVER_N);
    var i;
    for (i = 0; i < n; i++) out.push(imgs[i]);
    return out;
  }

  function firstRowCoversDecoded() {
    var imgs = firstRowCoverImgs();
    if (!imgs.length) return false;
    var i;
    for (i = 0; i < imgs.length; i++) {
      if (!(imgs[i].complete && imgs[i].naturalWidth > 0)) return false;
    }
    return true;
  }

  /** Boost SSR/first-row bandwidth claim via fetchpriority only. */
  function prioritizeFirstRowCovers() {
    var imgs = firstRowCoverImgs();
    var i;
    for (i = 0; i < imgs.length; i++) {
      try {
        imgs[i].setAttribute('fetchpriority', 'high');
        imgs[i].setAttribute('loading', 'eager');
        imgs[i].setAttribute('decoding', 'async');
      } catch (ePri) {
        /* ignore */
      }
    }
  }

  /**
   * Run cb once first-row covers are decoded (or after maxWaitMs).
   * Used to start deferred cover src promotion — not to gate personalization.
   */
  function whenFirstRowDecoded(cb, maxWaitMs) {
    if (typeof cb !== 'function') return;
    if (firstRowCoversDecoded()) {
      cb();
      return;
    }
    var imgs = firstRowCoverImgs();
    var pending = 0;
    var done = false;
    var finish = function () {
      if (done) return;
      done = true;
      cb();
    };
    var i;
    for (i = 0; i < imgs.length; i++) {
      if (imgs[i].complete && imgs[i].naturalWidth > 0) continue;
      pending++;
      (function (im) {
        var once = function () {
          im.removeEventListener('load', once);
          im.removeEventListener('error', once);
          pending--;
          if (pending <= 0) finish();
        };
        im.addEventListener('load', once);
        im.addEventListener('error', once);
      })(imgs[i]);
    }
    if (pending <= 0) {
      finish();
      return;
    }
    setTimeout(finish, typeof maxWaitMs === 'number' ? maxWaitMs : 8000);
  }


  function qs(sel, ctx) {
    return (ctx || document).querySelector(sel);
  }

  function qsa(sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  }

  function eventEl(t) {
    if (!t) return null;
    if (t.nodeType === 3) return t.parentElement;
    return t.nodeType === 1 ? t : null;
  }

  function withinIgnoreWindow() {
    return Date.now() - openedAt < CLOSE_IGNORE_MS;
  }

  function shopRoot() {
    return window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';
  }

  function loadData() {
    if (data) return data;
    var node = document.getElementById('wmn-best-sellers-data');
    if (!node) {
      data = [];
      return data;
    }
    try {
      data = JSON.parse(node.textContent || '[]') || [];
    } catch (e) {
      data = [];
    }
    return data;
  }

  function setBestSellersSeed(list) {
    if (!list || !list.length) return false;
    data = list;
    try {
      var node = document.getElementById('wmn-best-sellers-data');
      if (node) node.textContent = JSON.stringify(list);
    } catch (e) {
      /* ignore */
    }
    invalidateRankedCache();
    return true;
  }

  /**
   * Force storewide all-time best-selling order (Shopify order-count rank).
   * Uses /collections/all?sort_by=best-selling — not affinity / manual order.
   */
  function fetchBestSellersBySales() {
    var discoverRoot = document.getElementById('wmn-search-discover');
    var url = '';
    try {
      url =
        (discoverRoot &&
          discoverRoot.getAttribute('data-wmn-bs-sort-url')) ||
        '';
    } catch (e0) {
      url = '';
    }
    if (!url) {
      url =
        shopRoot() +
        'collections/all?view=wmn-bs-json&sort_by=best-selling';
    }
    return fetch(url, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json, text/plain, */*' }
    })
      .then(function (res) {
        if (!res || !res.ok) throw new Error('bs-sort-fetch');
        return res.text();
      })
      .then(function (text) {
        var parsed = null;
        try {
          parsed = JSON.parse(String(text || '').replace(/^\uFEFF/, ''));
        } catch (eParse) {
          parsed = null;
        }
        if (!Array.isArray(parsed) || parsed.length < 3) return null;
        return parsed;
      })
      .catch(function () {
        return null;
      });
  }

  function applyBestSellersSalesOrder(list) {
    if (!setBestSellersSeed(list)) return;
    // Refresh open cold Best Sellers grid (not personalized For you).
    if (
      open &&
      mode === 'bestsellers' &&
      !hasEntityPrefs() &&
      !fieldOwnsForYou() &&
      !fieldHasEnergyNodes()
    ) {
      try {
        paintItems(list, 'bestsellers', { forceWipe: false });
      } catch (ePaint) {
        /* ignore */
      }
    }
    reorderHomepageBestSellers(list);
  }

  /**
   * Homepage "Best Sellers" featured grid — reorder DOM to storewide sales rank.
   */
  function reorderHomepageBestSellers(list) {
    if (!list || !list.length) return;
    var roots = document.querySelectorAll('[data-wmn-bs-featured]');
    if (!roots || !roots.length) return;
    var order = {};
    var i;
    for (i = 0; i < list.length; i++) {
      if (list[i] && list[i].handle) order[list[i].handle] = i;
    }
    var ri;
    for (ri = 0; ri < roots.length; ri++) {
      var rootEl = roots[ri];
      var matrix =
        rootEl.querySelector('.product-list') ||
        rootEl.querySelector('.collection-matrix');
      if (!matrix) continue;
      var cells = Array.prototype.slice
        .call(matrix.children || [])
        .filter(function (el) {
          return (
            el &&
            el.nodeType === 1 &&
            el.classList &&
            el.classList.contains('thumbnail')
          );
        });
      if (!cells.length) continue;
      cells.sort(function (a, b) {
        var ha = handleFromHref(a);
        var hb = handleFromHref(b);
        var ia = ha != null && order[ha] != null ? order[ha] : 9999;
        var ib = hb != null && order[hb] != null ? order[hb] : 9999;
        return ia - ib;
      });
      for (i = 0; i < cells.length; i++) {
        matrix.appendChild(cells[i]);
      }
    }
  }

  function handleFromHref(el) {
    if (!el) return null;
    try {
      var a = el.querySelector('a[href*="/products/"]');
      if (!a) return null;
      var m = String(a.getAttribute('href') || '').match(
        /\/products\/([^/?#]+)/
      );
      return m ? decodeURIComponent(m[1]) : null;
    } catch (e) {
      return null;
    }
  }

  function warmBestSellersSalesOrder() {
    if (window.__wmnBsSalesWarm) return window.__wmnBsSalesWarm;
    window.__wmnBsSalesWarm = fetchBestSellersBySales().then(function (list) {
      if (list) applyBestSellersSalesOrder(list);
      return list;
    });
    return window.__wmnBsSalesWarm;
  }

  function affinityMap() {
    if (window.wmnEngagement && typeof window.wmnEngagement.getAffinity === 'function') {
      try {
        return window.wmnEngagement.getAffinity();
      } catch (e) {
        return null;
      }
    }
    return null;
  }

  function invalidateRankedCache() {
    rankedCache = null;
    rankedCacheAt = 0;
    settleRankPrimed = false;
  }

  function rankedBestSellers() {
    var now = Date.now();
    if (rankedCache && now - rankedCacheAt < RANK_CACHE_MS) {
      return rankedCache;
    }
    var t0 = performance.now();
    var items = loadData();
    var loadMs = performance.now() - t0;
    // Cold Best Sellers must stay all-time sales order — never affinity shuffle.
    // Affinity only applies once personalization owns the panel (For you).
    if (
      !hasEntityPrefs() &&
      !fieldOwnsForYou() &&
      !fieldHasEnergyNodes() &&
      !hasSessionFocusPrimary()
    ) {
      rankedCache = items;
      rankedCacheAt = now;
      return rankedCache;
    }
    if (
      window.wmnEngagement &&
      typeof window.wmnEngagement.rankByAffinity === 'function'
    ) {
      try {
        var tRank = performance.now();
        rankedCache = window.wmnEngagement.rankByAffinity(items);
        var rankMs = performance.now() - tRank;
        rankedCacheAt = now;
        return rankedCache;
      } catch (err) {
        return items;
      }
    }
    return items;
  }

  /**
   * Rank once after settle and keep the list for clear / remount paints.
   * Clear path should call paintCachedForYou() — not invalidate + re-rank.
   * Do NOT lock settleRankPrimed when lean/committed but the field has no
   * energy nodes yet (graph not ready → empty inject) — otherwise the blues
   * seed order sticks and graph-load upgrade is skipped.
   */
  function primeSettleRankedCache() {
    rankedCache = null;
    rankedCacheAt = 0;
    var list = rankedBestSellers();
    var nodesReady = fieldHasEnergyNodes();
    if (fieldOwnsForYou() && !nodesReady) {
      settleRankPrimed = false;
      // Keep the list for paint, but allow graph-load to re-rank.
      rankedCache = list || rankedCache;
      rankedCacheAt = Date.now();
      return list || [];
    }
    settleRankPrimed = !!(list && list.length);
    return list || [];
  }

  function fieldHasEnergyNodes() {
    try {
      if (
        !window.wmnIntentField ||
        typeof window.wmnIntentField.getField !== 'function'
      ) {
        return false;
      }
      var f = window.wmnIntentField.getField();
      return !!(f && f.nodes && Object.keys(f.nodes).length > 0);
    } catch (e) {
      return false;
    }
  }

  /**
   * Graph arrived after typed settles — reinject active stem and remount For you.
   */
  function refreshFieldAfterGraphLoad() {
    try {
      if (
        !window.wmnIntentField ||
        typeof window.wmnIntentField.refreshSearchInject !== 'function'
      ) {
        return false;
      }
      var f = window.wmnIntentField.getField();
      var stem = f && f.query && f.query.stem;
      if (!stem) return false;
      var hadNodes = fieldHasEnergyNodes();
      window.wmnIntentField.refreshSearchInject({
        query: stem,
        results: lastResultsForSignal || []
      });
      var nowHas = fieldHasEnergyNodes();
      if (!nowHas) return false;
      settleRankPrimed = false;
      rankedCache = null;
      rankedCacheAt = 0;
      if (open && mode !== 'results') {
        paintCachedForYou();
      } else {
        try {
          window.__wmnTypedPrefsDirty = true;
        } catch (eDirty) {
          /* ignore */
        }
        primeSettleRankedCache();
      }
      return true;
    } catch (e) {
      return false;
    }
  }

  function paintCachedForYou(opts) {
    opts = opts || {};
    var list =
      settleRankPrimed && rankedCache && rankedCache.length
        ? rankedCache
        : primeSettleRankedCache();
    if (!opts.keepPaintKey) lastPaintKey = '';
    paintItems(list, 'bestsellers', opts);
    return list;
  }

  /** Instant pool for typed search — never runs rankByAffinity on the keystroke path. */
  function localPool() {
    return mergePools(lastBestsellersPaint, loadData());
  }

  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function handleFromUrl(url) {
    var m = String(url || '').match(/\/products\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : '';
  }

  function mapSearchResult(item) {
    if (!item || item.object_type && item.object_type !== 'product') return null;
    var handle = item.handle || handleFromUrl(item.url);
    if (!handle && !item.url) return null;
    var title = item.title || '';
    var titleL = title.toLowerCase();
    if (titleL.indexOf('gift voucher') !== -1 || titleL.indexOf('gift card') !== -1) {
      return null;
    }
    var img = item.thumbnail || item.image || null;
    if (img === 'NULL' || img === '""') img = null;
    // search.json is slim (no tags/genres) — resolve genre slugs from catalog graph.
    var genres = [];
    if (Array.isArray(item.genres) && item.genres.length) {
      genres = item.genres.slice();
    } else if (
      handle &&
      window.wmnCatalogGraph &&
      typeof window.wmnCatalogGraph.resolveProduct === 'function' &&
      typeof window.wmnCatalogGraph.getData === 'function' &&
      window.wmnCatalogGraph.getData()
    ) {
      try {
        var resolved = window.wmnCatalogGraph.resolveProduct(handle);
        if (resolved && resolved.genres && resolved.genres.length) {
          genres = resolved.genres.slice();
        }
      } catch (e) {
        /* ignore */
      }
    }
    return {
      handle: handle,
      title: title,
      artist: item.artist || item.type || '',
      imprint: item.imprint || item.vendor || '',
      url: item.url,
      image: img,
      genres: genres,
      tags: item.tags
    };
  }

  function exactGenreSlug(query) {
    if (
      !window.wmnCatalogGraph ||
      typeof window.wmnCatalogGraph.exactGenreFromQuery !== 'function'
    ) {
      return null;
    }
    try {
      return window.wmnCatalogGraph.exactGenreFromQuery(query) || null;
    } catch (e) {
      return null;
    }
  }

  function productMatchesExactGenre(handle, genreSlug, title) {
    if (!handle || !genreSlug) return false;
    if (
      window.wmnEngagement &&
      typeof window.wmnEngagement.becauseProductMatchesGenre === 'function'
    ) {
      try {
        if (window.wmnEngagement.becauseProductMatchesGenre(handle, genreSlug)) {
          return true;
        }
      } catch (e0) {
        /* fall through */
      }
    }
    // Fallback before engagement/graph warm: title/handle word only.
    var text = (
      String(title || '') +
      ' ' +
      String(handle || '').replace(/-/g, ' ')
    ).toLowerCase();
    var gWords = String(genreSlug).replace(/-/g, ' ').trim();
    if (!gWords) return false;
    if (gWords.indexOf(' ') === -1) {
      return new RegExp(
        '\\b' + gWords.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\b',
        'i'
      ).test(text);
    }
    return text.indexOf(gWords) !== -1;
  }

  function filterExactGenreItems(query, items) {
    var slug = exactGenreSlug(query);
    if (!slug || !items || !items.length) return items;
    var out = [];
    var i;
    for (i = 0; i < items.length; i++) {
      var it = items[i];
      if (!it || !it.handle) continue;
      if (productMatchesExactGenre(it.handle, slug, it.title)) out.push(it);
    }
    return out;
  }

  function genreCollectionUrl(genreSlug) {
    if (!genreSlug) return '';
    var root = '/';
    try {
      if (typeof shopRoot === 'function') {
        var sr = shopRoot();
        if (sr) root = sr.charAt(sr.length - 1) === '/' ? sr : sr + '/';
      }
    } catch (e) {
      /* ignore */
    }
    return root + 'collections/all/' + encodeURIComponent(genreSlug);
  }

  /**
   * Graph-powered ranking for natural queries (genre/country/artist/"like X").
   * Returns { items, scores, intent } or null when graph cannot help.
   */
  function graphRankQuery(query, opts) {
    opts = opts || {};
    if (
      !window.wmnCatalogGraph ||
      typeof window.wmnCatalogGraph.graphSearch !== 'function' ||
      typeof window.wmnCatalogGraph.getData !== 'function' ||
      !window.wmnCatalogGraph.getData()
    ) {
      return null;
    }
    var result;
    try {
      result = window.wmnCatalogGraph.graphSearch(query, {
        limit: Math.max(GRID_LIMIT * 2, 36),
        seedHandles: opts.seedHandles || [],
        likeLane: opts.likeLane || null
      });
    } catch (e) {
      return null;
    }
    if (!result || !result.handles || !result.handles.length) {
      // Still return intent so callers can seed "like X" from Shopify hits.
      if (result && result.intent && (result.intent.entities.length || result.intent.like)) {
        return {
          items: [],
          scores: {},
          intent: result.intent,
          empty: true
        };
      }
      return null;
    }
    var items = [];
    var i;
    for (i = 0; i < result.handles.length; i++) {
      var card = null;
      if (typeof window.wmnCatalogGraph.productCard === 'function') {
        try {
          card = window.wmnCatalogGraph.productCard(result.handles[i]);
        } catch (eCard) {
          card = null;
        }
      }
      if (card) items.push(card);
    }
    return {
      items: items,
      scores: result.scores || {},
      intent: result.intent,
      empty: false
    };
  }

  function likePhraseFromQuery(query) {
    if (
      window.wmnCatalogGraph &&
      typeof window.wmnCatalogGraph.parseSearchIntent === 'function' &&
      window.wmnCatalogGraph.getData &&
      window.wmnCatalogGraph.getData()
    ) {
      try {
        var intent = window.wmnCatalogGraph.parseSearchIntent(query);
        if (intent && intent.like && intent.like.phrase) return intent.like.phrase;
      } catch (e) {
        /* fall through */
      }
    }
    var m = String(query || '').match(
      /\b(?:sounds?\s+like|similar\s+to|like)\s+(.+)$/i
    );
    return m && m[1] ? String(m[1]).replace(/^\s+|\s+$/g, '') : '';
  }

  /** Collect Shopify/local hits that match a "like Artist" phrase as graph seeds. */
  function seedHandlesFromItems(phrase, items) {
    if (!phrase || !items || !items.length) return [];
    var norm =
      window.wmnSearchFuzzy && typeof window.wmnSearchFuzzy.normalize === 'function'
        ? window.wmnSearchFuzzy.normalize(phrase)
        : String(phrase).toLowerCase();
    if (!norm) return [];
    var out = [];
    var i;
    for (i = 0; i < items.length && out.length < 6; i++) {
      var it = items[i];
      if (!it || !it.handle) continue;
      var hay = (
        (it.title || '') +
        ' ' +
        (it.artist || '') +
        ' ' +
        (it.handle || '').replace(/-/g, ' ')
      ).toLowerCase();
      if (hay.indexOf(norm) !== -1) out.push(it.handle);
    }
    return out;
  }

  function queryClickBoostFn() {
    return window.wmnEngagement &&
      typeof window.wmnEngagement.queryClickBoost === 'function'
      ? window.wmnEngagement.queryClickBoost
      : null;
  }

  /**
   * Pin handles previously clicked for this query to the front.
   * Soft score deltas alone lose to fuzzy position prior (Desert→Timchar).
   */
  function pinQueryClickHandles(query, ranked) {
    if (
      !window.wmnEngagement ||
      typeof window.wmnEngagement.queryClickTopHandles !== 'function'
    ) {
      return ranked || [];
    }
    var tops = [];
    try {
      tops = window.wmnEngagement.queryClickTopHandles(query, 6) || [];
    } catch (eTops) {
      tops = [];
    }
    if (!tops.length) return ranked || [];
    var byHandle = {};
    var i;
    for (i = 0; i < (ranked || []).length; i++) {
      if (ranked[i] && ranked[i].handle) byHandle[ranked[i].handle] = ranked[i];
    }
    var pinned = [];
    var seen = {};
    for (i = 0; i < tops.length; i++) {
      var h = tops[i];
      if (!h || seen[h]) continue;
      var card = byHandle[h];
      if (!card && typeof cardFromHandle === 'function') {
        try {
          card = cardFromHandle(h);
        } catch (eCard) {
          card = null;
        }
      }
      if (!card) continue;
      seen[h] = true;
      pinned.push(card);
    }
    if (!pinned.length) return ranked || [];
    var rest = [];
    for (i = 0; i < (ranked || []).length; i++) {
      var it = ranked[i];
      if (!it || !it.handle || seen[it.handle]) continue;
      seen[it.handle] = true;
      rest.push(it);
    }
    var out = pinned.concat(rest);
    if (out.length > GRID_LIMIT) out = out.slice(0, GRID_LIMIT);
    return out;
  }

  /**
   * Re-rank candidates with learned query→handle click weights.
   * Caps leapfrog so exact artist pins stay above related.
   */
  function applyQueryClickRerank(query, ranked, opts) {
    opts = opts || {};
    var boostFn = queryClickBoostFn();
    if (!boostFn || !ranked || ranked.length < 2) {
      return pinQueryClickHandles(query, ranked || []);
    }
    var pinExact = !!opts.pinExact;
    var exactSlug = opts.exactArtistSlug || '';
    var exact = [];
    var rest = [];
    var i;
    if (pinExact && exactSlug && window.wmnCatalogGraph && window.wmnCatalogGraph.getData) {
      var data = window.wmnCatalogGraph.getData();
      for (i = 0; i < ranked.length; i++) {
        var it = ranked[i];
        var a =
          it &&
          it.handle &&
          data &&
          data.products &&
          data.products[it.handle] &&
          data.products[it.handle].a;
        if (a === exactSlug) exact.push(it);
        else rest.push(it);
      }
    } else {
      rest = ranked.slice();
    }
    if (rest.length < 2) {
      return pinQueryClickHandles(query, ranked);
    }
    var n = rest.length;
    var decorated = rest.map(function (item, index) {
      var h = (item && item.handle) || '';
      return {
        item: item,
        score: (n - index) * 12 + (boostFn(query, h) || 0)
      };
    });
    decorated.sort(function (a, b) {
      return b.score - a.score;
    });
    var out = exact.concat(
      decorated.map(function (row) {
        return row.item;
      })
    );
    if (out.length > GRID_LIMIT) out = out.slice(0, GRID_LIMIT);
    // Pins win over soft score — one Timchar click must beat alphabetical desert pack.
    return pinQueryClickHandles(query, out);
  }

  function fuzzyRank(query, items, opts) {
    opts = opts || {};
    var ranked;
    if (
      window.wmnSearchFuzzy &&
      typeof window.wmnSearchFuzzy.rank === 'function'
    ) {
      ranked = window.wmnSearchFuzzy.rank(query, items, {
        limit: GRID_LIMIT,
        requireMatch: true,
        affinity: opts.skipAffinity ? null : affinityMap()
      });
    } else {
      var q = String(query || '').toLowerCase();
      ranked = [];
      for (var i = 0; i < items.length && ranked.length < GRID_LIMIT; i++) {
        var it = items[i];
        if (!it) continue;
        var hay = (
          (it.title || '') +
          ' ' +
          (it.artist || '') +
          ' ' +
          (it.handle || '')
        ).toLowerCase();
        if (hay.indexOf(q) !== -1) ranked.push(it);
      }
    }

    ranked = filterExactGenreItems(query, ranked);

    // Graph intent: merge edge-expanded candidates and re-rank.
    var seedHandles = opts.seedHandles || [];
    if (!seedHandles.length) {
      var likePhrase = likePhraseFromQuery(query);
      if (likePhrase) seedHandles = seedHandlesFromItems(likePhrase, items || []);
    }
    var graph = graphRankQuery(query, {
      seedHandles: seedHandles,
      likeLane: opts.likeLane || null
    });
    var artistOnlyIntent = false;
    var exactArtistSlug = '';
    if (graph && graph.intent && graph.intent.entities && graph.intent.entities.length) {
      artistOnlyIntent =
        !graph.intent.like &&
        graph.intent.entities.every(function (e) {
          return e && String(e.id || '').indexOf('artist:') === 0;
        });
      if (artistOnlyIntent && graph.intent.entities[0]) {
        exactArtistSlug = String(graph.intent.entities[0].id).split(':').slice(1).join(':');
      }
    }
    var clickBoost = queryClickBoostFn();
    if (graph && graph.items && graph.items.length) {
      var gScores = graph.scores || {};
      if (artistOnlyIntent && ranked && ranked.length) {
        // Exact fuzzy hits (the named artist) stay pinned on top. Graph
        // related artists append after — never leapfrog Coltrane via
        // engagement / tie-break (Guillaume Indo-Jazz bug).
        var pinned = ranked.slice();
        var pinnedH = {};
        var pi;
        for (pi = 0; pi < pinned.length; pi++) {
          if (pinned[pi] && pinned[pi].handle) pinnedH[pinned[pi].handle] = true;
        }
        // Thin-shelf densify (Cash / Gorbea) scores ~100–110 — must not use the
        // strict 100 bar in a way that drops the whole densified salsa/country pack.
        var densifying = !!(
          graph.intent &&
          ((graph.intent.densifyGenres && graph.intent.densifyGenres.length) ||
            (graph.intent.densifyArtists && graph.intent.densifyArtists.length) ||
            (graph.intent.densifyEmb && graph.intent.densifyEmb.length))
        );
        var relatedMin = densifying ? 55 : 100;
        var related = [];
        for (pi = 0; pi < graph.items.length; pi++) {
          var git = graph.items[pi];
          if (!git || !git.handle || pinnedH[git.handle]) continue;
          // Skip weak related when we already have the exact artist hit.
          if (exactArtistSlug && (gScores[git.handle] || 0) < relatedMin) continue;
          related.push(git);
        }
        related.sort(function (a, b) {
          var ca = clickBoost ? clickBoost(query, a.handle || '') : 0;
          var cb = clickBoost ? clickBoost(query, b.handle || '') : 0;
          return (
            (gScores[b.handle] || 0) +
            cb -
            ((gScores[a.handle] || 0) + ca) ||
            ((a.handle || '') < (b.handle || '') ? -1 : 1)
          );
        });
        ranked = pinned.concat(related);
      } else {
        ranked = mergePools(graph.items, ranked || []);
        var decoratedG = ranked.map(function (item, index) {
          var h = item && item.handle;
          var gScore = h && gScores[h] != null ? gScores[h] : 0;
          var fuzzyPrior = Math.max(0, (ranked.length - index) * 10);
          var learned = clickBoost && h ? clickBoost(query, h) : 0;
          return { item: item, score: gScore * 1.4 + fuzzyPrior + learned };
        });
        decoratedG.sort(function (a, b) {
          return b.score - a.score;
        });
        ranked = decoratedG.map(function (row) {
          return row.item;
        });
      }
      if (ranked.length > GRID_LIMIT) ranked = ranked.slice(0, GRID_LIMIT);
    }

    if (opts.skipAffinity) {
      return applyQueryClickRerank(query, ranked, {
        pinExact: artistOnlyIntent,
        exactArtistSlug: exactArtistSlug
      });
    }

    // Blend fuzzy order with entity scoreHandle (final network results only).
    // Never let engagement pull related artists above an exact artist match.
    var scoreFn =
      window.wmnEngagement && typeof window.wmnEngagement.scoreHandle === 'function'
        ? window.wmnEngagement.scoreHandle
        : null;
    if (!scoreFn || !ranked.length) {
      return applyQueryClickRerank(query, ranked, {
        pinExact: artistOnlyIntent,
        exactArtistSlug: exactArtistSlug
      });
    }
    if (artistOnlyIntent && exactArtistSlug) {
      var exactTier = [];
      var relatedTier = [];
      var ri;
      for (ri = 0; ri < ranked.length; ri++) {
        var rit = ranked[ri];
        if (rit && rit.handle && graph && graph.scores && (graph.scores[rit.handle] || 0) >= 150) {
          exactTier.push(rit);
        } else if (
          rit &&
          window.wmnCatalogGraph &&
          window.wmnCatalogGraph.getData &&
          window.wmnCatalogGraph.getData() &&
          window.wmnCatalogGraph.getData().products[rit.handle] &&
          window.wmnCatalogGraph.getData().products[rit.handle].a === exactArtistSlug
        ) {
          exactTier.push(rit);
        } else {
          relatedTier.push(rit);
        }
      }
      var nRel = relatedTier.length;
      relatedTier = relatedTier
        .map(function (item, index) {
          return {
            item: item,
            score:
              (nRel - index) * 12 +
              (scoreFn(item.handle || '') || 0) +
              ((graph && graph.scores && graph.scores[item.handle]) || 0) * 0.15 +
              (clickBoost ? clickBoost(query, item.handle || '') : 0)
          };
        })
        .sort(function (a, b) {
          return b.score - a.score;
        })
        .map(function (row) {
          return row.item;
        });
      return pinQueryClickHandles(
        query,
        exactTier.concat(relatedTier).slice(0, GRID_LIMIT)
      );
    }
    var n = ranked.length;
    var decorated = ranked.map(function (item, index) {
      var gBoost = 0;
      if (graph && graph.scores && item && item.handle) {
        gBoost = (graph.scores[item.handle] || 0) * 0.15;
      }
      return {
        item: item,
        score:
          (n - index) * 12 +
          (scoreFn(item.handle || '') || 0) +
          gBoost +
          (clickBoost ? clickBoost(query, item.handle || '') : 0)
      };
    });
    decorated.sort(function (a, b) {
      return b.score - a.score;
    });
    return pinQueryClickHandles(
      query,
      decorated.map(function (row) {
        return row.item;
      })
    );
  }

  function markResultsEngaged(handle) {
    resultsEngaged = true;
    if (handle) cardEngaged[handle] = true;
  }

  function noteResultsPainted() {
    if (!resultsShownAt) resultsShownAt = Date.now();
  }

  function resetResultsSession() {
    resultsShownAt = 0;
    resultsEngaged = false;
    cardSeenAt = {};
    cardEngaged = {};
    scrollPastDone = {};
  }

  function isBounceClear() {
    if (!resultsShownAt) return false;
    return Date.now() - resultsShownAt < BOUNCE_CLEAR_MS && !resultsEngaged;
  }

  function emitSkip(handle, reason, dwellMs) {
    if (!handle) return;
    if (
      window.wmnIntentField &&
      typeof window.wmnIntentField.onSkip === 'function'
    ) {
      try {
        window.wmnIntentField.onSkip(handle, {
          reason: reason || 'skip',
          dwellMs: dwellMs
        });
      } catch (e) {
        /* ignore */
      }
    }
    if (
      window.wmnIntentAnalytics &&
      typeof window.wmnIntentAnalytics.log === 'function'
    ) {
      try {
        window.wmnIntentAnalytics.log({
          type: 'skip',
          handle: handle,
          dwellMs: dwellMs != null ? dwellMs : null,
          mode: mode,
          meta: { reason: reason || 'skip' }
        });
      } catch (e2) {
        /* ignore */
      }
    }
  }

  function algoQueryAllowed(query) {
    try {
      if (
        window.wmnQueryGuard &&
        typeof window.wmnQueryGuard.isBlocked === 'function' &&
        window.wmnQueryGuard.isBlocked(query)
      ) {
        return false;
      }
    } catch (eGuard) {
      /* ignore */
    }
    return true;
  }

  function commitSearchSignal(query) {
    var norm = normalizeQuery(query);
    if (!norm || norm.length < 2 || norm === lastSearchSignal) return;
    // Dirty / abusive stems must not train For You, intent field, or prefs.
    if (!algoQueryAllowed(norm)) {
      lastSearchSignal = norm;
      return;
    }
    lastSearchSignal = norm;
    lastFlushedQuery = norm;

    // Warm collective query→click boosts for this settle (next rank uses them).
    if (
      window.wmnEngagement &&
      typeof window.wmnEngagement.ensureQueryClickCollective === 'function'
    ) {
      try {
        window.wmnEngagement.ensureQueryClickCollective(norm);
      } catch (eQc) {
        /* ignore */
      }
    }

    // Analytics settle count first (independent of ranking brain)
    if (
      window.wmnIntentAnalytics &&
      typeof window.wmnIntentAnalytics.noteSearchSettle === 'function'
    ) {
      try {
        var shown = [];
        var results = lastResultsForSignal || [];
        for (var i = 0; i < results.length && shown.length < 12; i++) {
          if (results[i] && results[i].handle) shown.push(results[i].handle);
        }
        window.wmnIntentAnalytics.noteSearchSettle(norm, {
          mode: mode,
          shownHandles: shown
        });
      } catch (e2) {
        /* ignore */
      }
    }

    // Field is the ranking brain — settle into the energy field first.
    var fieldMode = 'explore';
    if (
      window.wmnIntentField &&
      typeof window.wmnIntentField.onSearchSettle === 'function'
    ) {
      try {
        window.wmnIntentField.onSearchSettle(norm, {
          results: lastResultsForSignal || []
        });
        if (typeof window.wmnIntentField.getMode === 'function') {
          fieldMode = window.wmnIntentField.getMode() || 'explore';
        }
      } catch (e3) {
        /* ignore */
      }
    }

    // One brain: lean/committed — field owns ranking; do NOT bump entities/focus.
    // Explore: always soft-feed recordSearch (graph + visible results) even when
    // onSearchSettle already injected field nodes — that was skipping multi-signal.
    if (fieldMode === 'lean' || fieldMode === 'committed') {
      try {
        window.__wmnTypedPrefsDirty = true;
      } catch (eDirty) {
        /* ignore */
      }
      // Rank once here; paint now only when already on For you chrome.
      // During results mode, prime cache only — painting bestsellers would
      // flash "For you" over Results (H6) and race searchAndRender.
      primeSettleRankedCache();
      // Never paint For you over an in-progress typed query (incl. misses).
      if (open && mode !== 'results' && !holdSearchResultsPane()) {
        paintForYouOnSettle(fieldMode);
        try {
          window.__wmnTypedPrefsDirty = false;
        } catch (eClear) {
          /* ignore */
        }
      }
      return;
    }

    // Explore (incl. first settle with energy): multi-signal preference bump.
    if (
      window.wmnEngagement &&
      typeof window.wmnEngagement.recordSearch === 'function'
    ) {
      try {
        window.wmnEngagement.recordSearch(norm, {
          results: lastResultsForSignal || []
        });
      } catch (e) {
        /* ignore */
      }
    }

    // If field already has energy from this settle, prime For you for clear.
    if (fieldHasEnergyNodes()) {
      try {
        window.__wmnTypedPrefsDirty = true;
      } catch (eDirty2) {
        /* ignore */
      }
      primeSettleRankedCache();
      if (open && mode !== 'results' && !holdSearchResultsPane()) {
        paintForYouOnSettle(fieldMode);
        try {
          window.__wmnTypedPrefsDirty = false;
        } catch (eClear2) {
          /* ignore */
        }
      }
    }
  }

  /**
   * Lean/committed settle: paint For you from the settle-primed rank cache.
   * Does not re-rank — primeSettleRankedCache() already ran in commitSearchSignal.
   */
  function paintForYouOnSettle(fieldMode) {
    if (!open || holdSearchResultsPane()) return;
    paintCachedForYou();
    logSettlePaint(fieldMode);
    resetVisibilityTracking();
  }

  function logSettlePaint(fieldMode) {
    if (
      !window.wmnIntentAnalytics ||
      typeof window.wmnIntentAnalytics.log !== 'function'
    ) {
      return;
    }
    try {
      var qSettles = null;
      if (
        lastFlushedQuery &&
        typeof window.wmnIntentAnalytics.getSettleCount === 'function'
      ) {
        qSettles = window.wmnIntentAnalytics.getSettleCount(lastFlushedQuery);
      }
      window.wmnIntentAnalytics.log({
        type: 'settle_paint',
        q: lastFlushedQuery || null,
        qSettles: qSettles,
        mode: fieldMode || mode || 'bestsellers',
        shownHandles: gridShownHandles(),
        topEntities: topEntitiesForLog()
      });
    } catch (e) {
      /* ignore */
    }
  }

  function gridShownHandles() {
    var out = [];
    if (!grid) return out;
    var cards = grid.querySelectorAll('[data-wmn-handle]');
    for (var i = 0; i < cards.length && out.length < 12; i++) {
      var h = cards[i].getAttribute('data-wmn-handle');
      if (h) out.push(h);
    }
    return out;
  }

  function topEntitiesForLog() {
    try {
      if (
        window.wmnIntentAnalytics &&
        window.wmnEngagement &&
        typeof window.wmnEngagement.getEntityPrefs === 'function'
      ) {
        // buildCloudProfile / log normalize will also fill tops; pass none.
      }
    } catch (e) {
      /* ignore */
    }
    return null;
  }

  function logClearRemount() {
    if (
      !window.wmnIntentAnalytics ||
      typeof window.wmnIntentAnalytics.log !== 'function'
    ) {
      return;
    }
    try {
      var qSettles = null;
      if (
        lastFlushedQuery &&
        typeof window.wmnIntentAnalytics.getSettleCount === 'function'
      ) {
        qSettles = window.wmnIntentAnalytics.getSettleCount(lastFlushedQuery);
      }
      window.wmnIntentAnalytics.log({
        type: 'clear_remount',
        q: lastFlushedQuery || null,
        qSettles: qSettles,
        mode: mode || 'bestsellers',
        shownHandles: gridShownHandles(),
        topEntities: topEntitiesForLog()
      });
    } catch (e) {
      /* ignore */
    }
  }

  function scheduleSearchSignal(query) {
    pendingSearchSignalQuery = query;
    if (searchSignalTimer) clearTimeout(searchSignalTimer);
    // Fixed debounce — do not idle-flush early (fleeting keys must not dominate).
    searchSignalTimer = setTimeout(function () {
      searchSignalTimer = null;
      commitSearchSignal(query);
    }, SEARCH_SIGNAL_MS);
  }

  function scheduleIdleSearchFlush(norm) {
    var gen = ++searchSignalIdleGen;
    var run = function () {
      if (gen !== searchSignalIdleGen) return;
      if (!pendingSearchSignalQuery) return;
      if (normalizeQuery(pendingSearchSignalQuery) !== norm) return;
      // Still pending — flush early while the main thread is idle.
      flushSearchSignal(pendingSearchSignalQuery);
    };
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(run, { timeout: SEARCH_SIGNAL_MS });
    } else if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(run);
      });
    }
  }

  function flushSearchSignal(query) {
    searchSignalIdleGen += 1; // cancel pending idle flushes
    if (searchSignalTimer) {
      clearTimeout(searchSignalTimer);
      searchSignalTimer = null;
    }
    pendingSearchSignalQuery = '';
    if (query) commitSearchSignal(query);
  }

  function consumeTypedPrefsDirty() {
    var dirty = false;
    try {
      dirty = !!window.__wmnTypedPrefsDirty;
      window.__wmnTypedPrefsDirty = false;
    } catch (e) {
      dirty = false;
    }
    return dirty;
  }

  /** Current typed query in the open discover search field (if any). */
  function activeDiscoverQuery() {
    try {
      var active = document.activeElement;
      if (isHeaderSearchInput(active)) {
        var aq = inputValue(active);
        if (aq) return aq;
      }
      var inp = document.querySelector(
        '.wmn-header__search input[name="q"], .header_search_form input[name="q"]'
      );
      return inputValue(inp);
    } catch (e) {
      return '';
    }
  }

  /**
   * While the user still has a typed query, never replace Results / empty
   * apology with personalized For you (Guns N' Roses → Indian Classical bug).
   */
  function holdSearchResultsPane() {
    return !!(open && activeDiscoverQuery());
  }

  function remountForYouFromTypedPrefs() {
    // Nuclear: invalidate + forceWipe so SSR/reorder cannot keep seed order.
    if (!open || holdSearchResultsPane()) return;
    paintCachedForYouNuclear('typed-prefs');
    logClearRemount();
    resetVisibilityTracking();
  }

  function paintForYouNow(reason) {
    if (holdSearchResultsPane()) return;
    paintCachedForYouNuclear(reason || 'for-you');
  }

  function paintEmptyState() {
    if (settleRankPrimed && rankedCache && rankedCache.length) {
      paintCachedForYou();
      return;
    }
    if (hasEntityPrefs() || fieldOwnsForYou() || fieldHasEnergyNodes()) {
      paintCachedForYouNuclear('empty-open');
      return;
    }
    prioritizeFirstRowCovers();
    // Prefer sales-sorted seed when warm fetch already finished
    warmBestSellersSalesOrder().then(function (sorted) {
      if (
        !open ||
        mode !== 'bestsellers' ||
        hasEntityPrefs() ||
        fieldOwnsForYou() ||
        fieldHasEnergyNodes()
      ) {
        return;
      }
      if (sorted && sorted.length) {
        paintItems(sorted, 'bestsellers', { skipHydrate: true });
      }
    });
    paintItems(loadData(), 'bestsellers', { skipHydrate: true });
  }

  /**
   * Session focus primary (genre/country/region) — same driver as homepage scorer.
   */
  function hasSessionFocusPrimary() {
    try {
      if (
        !window.wmnEngagement ||
        typeof window.wmnEngagement.getFocus !== 'function'
      ) {
        return false;
      }
      var f = window.wmnEngagement.getFocus();
      if (!f) return false;
      var kind = f.primaryKind || '';
      if (kind === 'country' && f.primaryCountry) return true;
      if (kind === 'region' && f.primaryRegion) return true;
      if (kind === 'genre' && (f.primaryGenre || (f.genres && f.genres[0]))) {
        return true;
      }
      if (!kind) {
        if (f.primaryCountry || f.primaryRegion) return true;
        if (f.primaryGenre || (f.genres && f.genres[0])) return true;
      }
    } catch (e) {
      /* ignore */
    }
    return false;
  }

  /**
   * Phase 6: Discover empty For you uses the homepage catalog scorer.
   * @returns {object[]|null}
   */
  function sharedCatalogForYouPicks() {
    try {
      if (
        !window.wmnEngagement ||
        typeof window.wmnEngagement.scoreCatalogForYou !== 'function'
      ) {
        return null;
      }
      var built = window.wmnEngagement.scoreCatalogForYou({
        limit: 32,
        explore: false,
        pinRecent: true
      });
      if (built && built.picks && built.picks.length >= 3) {
        return built.picks;
      }
    } catch (e) {
      /* ignore */
    }
    return null;
  }

  /**
   * Always wipe/reorder For you when personalized energy exists.
   * Prefer settle-primed order from the typed query — do NOT blindly
   * invalidate+re-rank (dwell on seed bestsellers can pollute the field and
   * wash out a single cuba settle). Reinforce last stem only when re-ranking.
   * Phase 6: geo / rail-affinity / empty-open (+ session focus) → scoreCatalogForYou(32).
   */
  function paintCachedForYouNuclear(reason) {
    seedExpandGen += 1;
    lastPaintKey = '';
    var list = null;
    var path = 'rerank';

    // Shared homepage composition for rail / geo / empty-open / session focus
    var forceShared =
      reason === 'rail-affinity' ||
      reason === 'empty-open' ||
      (window.wmnEngagement &&
        typeof window.wmnEngagement.hasGeoForYouPrefs === 'function' &&
        window.wmnEngagement.hasGeoForYouPrefs()) ||
      hasSessionFocusPrimary();
    if (forceShared) {
      settleRankPrimed = false;
      rankedCache = null;
      rankedCacheAt = 0;
      list = sharedCatalogForYouPicks();
      if (list) path = 'catalog-shared';
    }

    if (!list && settleRankPrimed && rankedCache && rankedCache.length) {
      list = rankedCache;
      path = 'settleCache';
    } else if (!list) {
      try {
        if (
          lastFlushedQuery &&
          window.wmnIntentField &&
          typeof window.wmnIntentField.refreshSearchInject === 'function'
        ) {
          window.wmnIntentField.refreshSearchInject({
            query: lastFlushedQuery,
            results: lastResultsForSignal || []
          });
        }
      } catch (eRein) {
        /* ignore */
      }
      invalidateRankedCache();
      // Prefer catalog scorer over seed rankByAffinity when personalized
      if (hasEntityPrefs() || fieldOwnsForYou() || hasSessionFocusPrimary()) {
        list = sharedCatalogForYouPicks();
        if (list) path = 'catalog-shared';
      }
      if (!list) {
        list = rankedBestSellers();
        path = path === 'catalog-shared' ? path : 'rerank';
      }
    }
    // Prefer reorder+salvage when enough cards overlap — forceWipe only when
    // composition is mostly new (avoids re-download/re-decode of first row).
    var forceWipe = true;
    if (grid && list && list.length) {
      var map = existingCardMap();
      var overlap = 0;
      var oi;
      for (oi = 0; oi < list.length; oi++) {
        if (list[oi] && list[oi].handle && map[list[oi].handle]) overlap++;
      }
      if (overlap >= Math.ceil(list.length * 0.5)) forceWipe = false;
    }
    paintItems(list, 'bestsellers', { forceWipe: forceWipe });
    if (list && list.length) {
      rankedCache = list;
      rankedCacheAt = Date.now();
      settleRankPrimed = true;
    }
    return list;
  }

  function hasEntityPrefs() {
    try {
      if (
        window.wmnEngagement &&
        typeof window.wmnEngagement.getFocus === 'function'
      ) {
        var focus = window.wmnEngagement.getFocus();
        if (
          focus &&
          ((focus.artists && focus.artists.length) ||
            (focus.genres && focus.genres.length) ||
            (focus.handles && focus.handles.length))
        ) {
          return true;
        }
      }
      if (
        window.wmnEngagement &&
        typeof window.wmnEngagement.getEntityPrefs === 'function'
      ) {
        var prefs = window.wmnEngagement.getEntityPrefs() || {};
        return Object.keys(prefs).length > 0;
      }
    } catch (e) {
      /* ignore */
    }
    return false;
  }

  function fieldOwnsForYou() {
    try {
      if (
        window.wmnIntentField &&
        typeof window.wmnIntentField.ownsRanking === 'function'
      ) {
        return !!window.wmnIntentField.ownsRanking();
      }
      if (
        window.wmnIntentField &&
        typeof window.wmnIntentField.getMode === 'function'
      ) {
        var m = window.wmnIntentField.getMode();
        return m === 'lean' || m === 'committed';
      }
    } catch (e) {
      /* ignore */
    }
    return false;
  }

  var similarLabel = '';
  /** Lane explanation for results: { similarTo, genres[], countries[], artistLabel, densify }. */
  var explainState = null;

  var GENERIC_EXPLAIN_GENRES = {
    'world-music': 1,
    'world-music-compilation': 1,
    'world-compilation': 1
  };

  function entityLabel(kind, slug) {
    if (!slug) return '';
    try {
      if (
        window.wmnCatalogGraph &&
        typeof window.wmnCatalogGraph.entityDisplayName === 'function'
      ) {
        return window.wmnCatalogGraph.entityDisplayName(kind, slug) || '';
      }
    } catch (eLab) {
      /* ignore */
    }
    return String(slug)
      .replace(/-/g, ' ')
      .replace(/\b\w/g, function (c) {
        return c.toUpperCase();
      });
  }

  function firstSpecificGenreSlug(genres) {
    var list = genres || [];
    var i;
    for (i = 0; i < list.length; i++) {
      var g = String(list[i] || '').toLowerCase();
      if (g && !GENERIC_EXPLAIN_GENRES[g]) return g;
    }
    return list.length ? String(list[0] || '').toLowerCase() : '';
  }

  function setExplainState(next) {
    explainState = next || null;
  }

  function clearExplainState() {
    explainState = null;
  }

  /**
   * Shelf why line — e.g. "Similar to Tinariwen · Desert blues · Mali".
   * Teaches how the catalogue lane was chosen.
   */
  function formatExplainLine(state) {
    if (!state) return '';
    var parts = [];
    if (state.similarTo) {
      parts.push('Similar to ' + state.similarTo);
    } else if (state.artistLabel) {
      parts.push(state.artistLabel);
    }
    var gSlug = firstSpecificGenreSlug(state.genres);
    if (gSlug) {
      var gName = entityLabel('genre', gSlug);
      if (gName && parts.indexOf(gName) === -1) parts.push(gName);
    }
    var countries = state.countries || [];
    var ci;
    for (ci = 0; ci < countries.length && parts.length < 4; ci++) {
      var cSlug = String(countries[ci] || '').toLowerCase();
      if (!cSlug || cSlug === 'celtic') continue;
      var cName = entityLabel('country', cSlug);
      if (cName && parts.indexOf(cName) === -1) {
        parts.push(cName);
        break;
      }
    }
    if (parts.length < 2 && !state.similarTo) return '';
    return parts.join(' · ');
  }

  /**
   * Per-card why — product’s lane overlap with the active explain state.
   * Exact named-artist hits stay quiet; related / similar cards speak.
   */
  function explainWhyForHandle(handle, opts) {
    opts = opts || {};
    if (!handle || !explainState) return '';
    if (opts.exactArtist) return '';
    var g = window.wmnCatalogGraph;
    if (!g || typeof g.resolveProduct !== 'function') return '';
    var resolved = null;
    try {
      resolved = g.resolveProduct(handle) || {};
    } catch (eR) {
      return '';
    }
    var pGenres = resolved.genres || [];
    var pCountries = resolved.countries || [];
    var laneG = {};
    var i;
    var genres = explainState.genres || [];
    for (i = 0; i < genres.length; i++) {
      var gs = String(genres[i] || '').toLowerCase();
      if (gs && !GENERIC_EXPLAIN_GENRES[gs]) laneG[gs] = true;
    }
    var hitG = '';
    for (i = 0; i < pGenres.length; i++) {
      var pg = String(pGenres[i] || '').toLowerCase();
      if (laneG[pg]) {
        hitG = pg;
        break;
      }
      if (!hitG && pg && !GENERIC_EXPLAIN_GENRES[pg] && explainState.similarTo) {
        hitG = pg;
      }
    }
    if (!hitG && pGenres.length && explainState.densify) {
      hitG = firstSpecificGenreSlug(pGenres);
    }
    var laneC = {};
    var countries = explainState.countries || [];
    for (i = 0; i < countries.length; i++) {
      var cs = String(countries[i] || '').toLowerCase();
      if (cs) laneC[cs] = true;
    }
    var hitC = '';
    for (i = 0; i < pCountries.length; i++) {
      var pc = String(pCountries[i] || '').toLowerCase();
      if (laneC[pc] || (explainState.similarTo && pc && !hitC)) {
        hitC = pc;
        if (laneC[pc]) break;
      }
    }
    var bits = [];
    if (hitG) bits.push(entityLabel('genre', hitG));
    if (hitC) bits.push(entityLabel('country', hitC));
    if (!bits.length && explainState.similarTo) {
      return 'Neighbour';
    }
    return bits.join(' · ');
  }

  function explainStateFromSimilar(sim) {
    if (!sim) return null;
    return {
      similarTo: sim.similarTo || '',
      genres: (sim.genres || []).slice(),
      countries: (sim.countries || []).slice(),
      densify: false
    };
  }

  function explainStateFromGraphIntent(intent, opts) {
    opts = opts || {};
    if (!intent) return null;
    var genres = [];
    var countries = [];
    var artistLabel = '';
    var densify = !!(
      (intent.densifyGenres && intent.densifyGenres.length) ||
      (intent.densifyArtists && intent.densifyArtists.length)
    );
    if (intent.densifyGenres && intent.densifyGenres.length) {
      genres = intent.densifyGenres.slice();
    }
    var ents = intent.entities || [];
    var ei;
    for (ei = 0; ei < ents.length; ei++) {
      var id = String((ents[ei] && ents[ei].id) || '');
      var colon = id.indexOf(':');
      if (colon < 0) continue;
      var kind = id.slice(0, colon);
      var key = id.slice(colon + 1);
      if (kind === 'genre' && genres.indexOf(key) === -1) genres.push(key);
      if (kind === 'country' && countries.indexOf(key) === -1) countries.push(key);
      if (kind === 'artist' && !artistLabel) {
        artistLabel = entityLabel('artist', key) || opts.artistFallback || '';
      }
    }
    if (intent.like && intent.like.lane) {
      var lane = intent.like.lane;
      return {
        similarTo: lane.n || intent.like.phrase || '',
        genres: (lane.g && lane.g.length ? lane.g : genres).slice(),
        countries: (lane.c && lane.c.length ? lane.c : countries).slice(),
        densify: false
      };
    }
    if (!genres.length && !countries.length && !artistLabel) return null;
    return {
      similarTo: '',
      genres: genres,
      countries: countries,
      artistLabel: artistLabel,
      densify: densify
    };
  }

  function setPanelChrome(nextMode, hasItems, opts) {
    opts = opts || {};
    var missFb = !!opts.missFallback;
    mode = nextMode;
    if (nextMode !== 'results') {
      similarLabel = '';
      clearExplainState();
    }
    var personalized =
      nextMode !== 'results' &&
      (hasEntityPrefs() || fieldOwnsForYou() || fieldHasEnergyNodes());
    var explainLine =
      nextMode === 'results' && !missFb ? formatExplainLine(explainState) : '';
    var title =
      nextMode === 'results'
        ? missFb
          ? opts.missFallbackLabel || ''
          : similarLabel
            ? explainLine || 'Similar to ' + similarLabel
            : hasItems
              ? 'Results'
              : ''
        : personalized
          ? 'For you'
          : 'Best Sellers';
    // When title already carries the full explain string, why sits empty;
    // densify / genre Results keep "Results" + a why subtitle.
    var whyText = '';
    if (nextMode === 'results' && !missFb && hasItems) {
      if (similarLabel) {
        whyText = '';
      } else if (explainLine && explainLine.indexOf('Similar to ') !== 0) {
        whyText = explainLine;
      }
    }
    if (titleEl) {
      titleEl.textContent = title;
      if (missFb && title) {
        titleEl.classList.add('wmn-search-discover__title--rail');
      } else {
        titleEl.classList.remove('wmn-search-discover__title--rail');
      }
      if (whyText) {
        titleEl.classList.add('wmn-search-discover__title--explained');
      } else {
        titleEl.classList.remove('wmn-search-discover__title--explained');
      }
    }
    if (whyEl) {
      if (whyText) {
        whyEl.textContent = whyText;
        whyEl.hidden = false;
      } else {
        whyEl.textContent = '';
        whyEl.hidden = true;
      }
    }
    if (panelEl) {
      panelEl.setAttribute(
        'aria-label',
        nextMode === 'results'
          ? missFb || (!hasItems && !similarLabel)
            ? "Sorry, we don't have anything like that right now"
            : explainLine ||
              (similarLabel ? 'Similar to ' + similarLabel : 'Search results')
          : title
      );
    }
    if (emptyEl) {
      // Pending similar/live resolve: claim results mode without flashing apology.
      if (opts.suppressEmpty) {
        emptyEl.hidden = true;
      } else if (hasItems && !missFb) {
        // Miss fallback keeps the apology visible above the product rail.
        emptyEl.hidden = true;
      } else {
        emptyEl.textContent =
          nextMode === 'results'
            ? "Sorry, we don't have anything like that right now"
            : 'No recommendations to show yet.';
        emptyEl.hidden = false;
      }
    }
  }

  function isLikelyProductHandle(handle) {
    if (
      window.wmnEngagement &&
      typeof window.wmnEngagement.isLikelyProductHandle === 'function'
    ) {
      return !!window.wmnEngagement.isLikelyProductHandle(handle);
    }
    var h = String(handle || '').replace(/^\s+|\s+$/g, '');
    if (!h || h.indexOf('/') !== -1 || h.indexOf('.') !== -1) return false;
    if (/_\d+x\d*(?:$|[.?#])/i.test(h)) return false;
    return true;
  }

  /** Slim card from handle — graph first, then seed / local pools. */
  function cardFromHandle(handle) {
    var h = String(handle || '')
      .replace(/^\s+|\s+$/g, '')
      .toLowerCase();
    if (!h || !isLikelyProductHandle(h)) return null;
    var g = window.wmnCatalogGraph;
    if (g && typeof g.productCard === 'function') {
      try {
        var card = g.productCard(h);
        if (card && card.handle) return card;
      } catch (eCard) {
        /* fall through */
      }
    }
    var pools = [lastBestsellersPaint, loadData()];
    var pi;
    for (pi = 0; pi < pools.length; pi++) {
      var pool = pools[pi] || [];
      var i;
      for (i = 0; i < pool.length; i++) {
        if (
          pool[i] &&
          pool[i].handle &&
          String(pool[i].handle).toLowerCase() === h
        ) {
          return pool[i];
        }
      }
    }
    var root = shopRoot();
    if (root.slice(-1) !== '/') root += '/';
    var image = '';
    try {
      if (g && typeof g.getProductImage === 'function') {
        image = g.getProductImage(h) || '';
      }
    } catch (eImg) {
      /* ignore */
    }
    return {
      handle: h,
      title: h.replace(/-/g, ' '),
      url: root + 'products/' + encodeURIComponent(h),
      image: image
    };
  }

  /**
   * Empty search miss rail: recently viewed → For you → Best Sellers.
   * @returns {{items: object[], label: string}}
   */
  function missFallbackBundle() {
    try {
      if (
        window.wmnEngagement &&
        typeof window.wmnEngagement.readRecentlyViewedCookie === 'function'
      ) {
        var rv = window.wmnEngagement.readRecentlyViewedCookie() || [];
        var cards = [];
        var seen = {};
        var ri;
        for (ri = 0; ri < rv.length && cards.length < DISPLAY_LIMIT; ri++) {
          var c = cardFromHandle(rv[ri]);
          if (!c || !c.handle || seen[c.handle]) continue;
          seen[c.handle] = true;
          cards.push(c);
        }
        if (cards.length) {
          return { items: cards, label: 'Recently viewed' };
        }
      }
    } catch (eRv) {
      /* fall through */
    }

    if (
      hasEntityPrefs() ||
      fieldOwnsForYou() ||
      fieldHasEnergyNodes() ||
      hasSessionFocusPrimary()
    ) {
      var fy = sharedCatalogForYouPicks();
      if (fy && fy.length) {
        return { items: fy, label: 'For you' };
      }
    }

    var bs = rankedBestSellers() || loadData() || [];
    return {
      items: bs.slice(0, DISPLAY_LIMIT),
      label: 'Best Sellers'
    };
  }

  /** Handle-only signature — image URL changes must not remount the grid. */
  function itemsSignature(items, nextMode, opts) {
    var parts = [nextMode || '', hasEntityPrefs() ? 'fy' : 'bs'];
    if (opts && opts.missFallback) {
      parts.push('miss');
      parts.push(opts.missFallbackLabel || '');
    }
    if (nextMode === 'results') {
      parts.push(similarLabel || '');
      parts.push(formatExplainLine(explainState) || '');
    }
    var list = items || [];
    for (var i = 0; i < list.length; i++) {
      parts.push((list[i] && list[i].handle) || (list[i] && list[i].url) || '');
    }
    return parts.join('|');
  }

  function sameImageUrl(a, b) {
    if (!a || !b) return false;
    if (a === b) return true;
    var na = String(a).replace(/^https?:/, '');
    var nb = String(b).replace(/^https?:/, '');
    if (na === nb) return true;
    // Treat _200x / _400x (etc.) as the same asset — do not reload decoded covers.
    return (
      na.replace(/_\d+x(?=\.|\?)/, '_Xx') ===
      nb.replace(/_\d+x(?=\.|\?)/, '_Xx')
    );
  }

  /** Copy imageCache URLs onto ranked items so settle-primed covers can patch. */
  function syncItemImagesFromGraph(items) {
    var graph = window.wmnCatalogGraph;
    if (!graph || typeof graph.getProductImage !== 'function') return items || [];
    var list = items || [];
    var i;
    for (i = 0; i < list.length; i++) {
      var it = list[i];
      if (!it || !it.handle || it.image) continue;
      var cached = graph.getProductImage(it.handle);
      if (cached) it.image = cached;
    }
    return list;
  }

  /** Inject cover imgs from imageCache for every grid card (DOM-driven). */
  function patchGridCoversFromGraph() {
    if (!grid) return;
    var graph = window.wmnCatalogGraph;
    if (!graph || typeof graph.getProductImage !== 'function') return;
    var cards = grid.querySelectorAll('[data-wmn-handle]');
    var i;
    for (i = 0; i < cards.length; i++) {
      var handle = cards[i].getAttribute('data-wmn-handle');
      if (!handle) continue;
      var src = graph.getProductImage(handle);
      if (!src) continue;
      var img = cards[i].querySelector('img');
      if (img) {
        var cur = img.getAttribute('src');
        var pending = img.getAttribute('data-src');
        if (sameImageUrl(cur, src) || sameImageUrl(pending, src)) continue;
        if (cur && img.complete && img.naturalWidth > 0) continue;
        img.removeAttribute('data-src');
        img.setAttribute('loading', 'eager');
        img.setAttribute('decoding', 'async');
        img.setAttribute('src', src);
        continue;
      }
      var cover = cards[i].querySelector('.wmn-search-discover__cover');
      if (cover) {
        cover.innerHTML =
          '<img src="' +
          escapeHtml(src) +
          '" alt="" loading="eager" decoding="async" width="200" height="200" />';
      }
    }
  }

  function patchCardImages(items) {
    if (!grid) return;
    syncItemImagesFromGraph(items);
    var graph = window.wmnCatalogGraph;
    var byHandle = {};
    var i;
    for (i = 0; i < (items || []).length; i++) {
      var it = items[i];
      if (!it || !it.handle) continue;
      var src = it.image || null;
      if (
        !src &&
        graph &&
        typeof graph.getProductImage === 'function'
      ) {
        src = graph.getProductImage(it.handle) || null;
        if (src) it.image = src;
      }
      if (src) byHandle[it.handle] = src;
    }
    var cards = grid.querySelectorAll('[data-wmn-handle]');
    for (i = 0; i < cards.length; i++) {
      var handle = cards[i].getAttribute('data-wmn-handle');
      var src = handle && byHandle[handle];
      if (!src) continue;
      var img = cards[i].querySelector('img');
      if (img) {
        var cur = img.getAttribute('src');
        var pending = img.getAttribute('data-src');
        if (sameImageUrl(cur, src) || sameImageUrl(pending, src)) continue;
        // SSR / preloaded covers are already decoded — never bounce them.
        if (cur && img.complete && img.naturalWidth > 0) continue;
        img.removeAttribute('data-src');
        img.setAttribute('loading', 'eager');
        img.setAttribute('decoding', 'async');
        img.setAttribute('src', src);
        continue;
      }
      var cover = cards[i].querySelector('.wmn-search-discover__cover');
      if (cover) {
        cover.innerHTML =
          '<img src="' +
          escapeHtml(src) +
          '" alt="" loading="eager" decoding="async" width="200" height="200" />';
      }
    }
    // DOM-driven pass: covers any card whose ranked item lacked .image but cache has URL.
    patchGridCoversFromGraph();
    patchDiscoverPlayButtons();
  }

  function cardHtml(item, loading) {
    if (!item || !item.url) return '';
    // Default lazy — remounts must not decode 32 eager covers at once.
    // Below-fold: data-src only (no src). Native loading=lazy still requests every
    // row inside the fixed overflow panel and saturates Slow-4G (~4–5s first paint).
    var load = loading || 'lazy';
    var eager = load === 'eager';
    var img = item.image
      ? '<div class="wmn-search-discover__cover"><img ' +
        (eager
          ? 'src="' + escapeHtml(item.image) + '" '
          : 'data-src="' + escapeHtml(item.image) + '" ') +
        'alt="" loading="' +
        load +
        '" decoding="async" width="200" height="200" /></div>'
      : '<div class="wmn-search-discover__cover"></div>';
    var artistName = item.artist || '';
    if (window.wmnCardKicker && item.handle) {
      artistName = window.wmnCardKicker({
        handle: item.handle,
        title: item.title || '',
        artist: item.artist || ''
      });
    }
    var artist = artistName
      ? '<p class="wmn-search-discover__artist">' +
        escapeHtml(artistName) +
        '</p>'
      : '';
    var whyCard = '';
    if (mode === 'results' && item.handle && explainState) {
      var exactArtist = false;
      if (explainState.artistLabel && item.artist) {
        exactArtist =
          normalizeQuery(item.artist) === normalizeQuery(explainState.artistLabel);
      }
      // Named-artist densify: quiet on the seed artist’s own titles.
      if (
        explainState.densify &&
        explainState.artistLabel &&
        item.artist &&
        normalizeQuery(item.artist).indexOf(
          normalizeQuery(explainState.artistLabel).split(' ')[0] || ''
        ) === 0 &&
        normalizeQuery(item.artist) === normalizeQuery(explainState.artistLabel)
      ) {
        exactArtist = true;
      }
      var whyTxt = explainWhyForHandle(item.handle, { exactArtist: exactArtist });
      if (whyTxt) {
        whyCard =
          '<p class="wmn-search-discover__card-why">' +
          escapeHtml(whyTxt) +
          '</p>';
      }
    }
    var sp = spotifyPathForHandle(item.handle);
    var play = playButtonHtml(item.handle, sp);
    var handleAttr = item.handle
      ? ' data-wmn-handle="' + escapeHtml(item.handle) + '"'
      : '';
    var spAttr = sp ? ' data-wmn-spotify="' + escapeHtml(sp) + '"' : '';
    return (
      '<div class="wmn-search-discover__card-wrap"' +
      handleAttr +
      spAttr +
      (item.title
        ? ' data-wmn-title="' + escapeHtml(item.title) + '"'
        : '') +
      '>' +
      '<a class="wmn-search-discover__card" href="' +
      escapeHtml(item.url) +
      '"' +
      handleAttr +
      '>' +
      img +
      '<div class="wmn-search-discover__meta">' +
      artist +
      '<p class="wmn-search-discover__name">' +
      escapeHtml(item.title || '') +
      '</p>' +
      whyCard +
      '</div></a>' +
      play +
      '</div>'
    );
  }

  function spotifyPathForHandle(handle) {
    if (!handle) return '';
    try {
      if (
        window.wmnCatalogGraph &&
        typeof window.wmnCatalogGraph.getSpotifyPath === 'function'
      ) {
        return window.wmnCatalogGraph.getSpotifyPath(handle) || '';
      }
    } catch (e) {
      /* ignore */
    }
    return '';
  }

  function playButtonHtml(handle, sp) {
    if (!handle || !sp) return '';
    return (
      '<button type="button" class="wmn-card-cover__play" data-wmn-play data-wmn-listen data-wmn-handle="' +
      escapeHtml(handle) +
      '" data-wmn-spotify="' +
      escapeHtml(sp) +
      '" data-wmn-play-source="search-discover" aria-label="Play preview" aria-pressed="false">' +
      '<svg class="wmn-card-cover__play-icon wmn-card-cover__play-icon--play" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M8 5v14l11-7z"/></svg>' +
      '<svg class="wmn-card-cover__play-icon wmn-card-cover__play-icon--pause" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6 5h4v14H6zm8 0h4v14h-4z"/></svg>' +
      '</button>'
    );
  }

  /** After catalog graph loads, inject missing cover-play on already-painted cards. */
  function patchDiscoverPlayButtons() {
    if (!grid) return;
    var wraps = grid.querySelectorAll('.wmn-search-discover__card-wrap[data-wmn-handle]');
    for (var i = 0; i < wraps.length; i++) {
      var wrap = wraps[i];
      if (wrap.querySelector('[data-wmn-play]')) continue;
      var handle = (wrap.getAttribute('data-wmn-handle') || '').trim();
      var sp = spotifyPathForHandle(handle);
      if (!sp) continue;
      wrap.setAttribute('data-wmn-spotify', sp);
      wrap.insertAdjacentHTML('beforeend', playButtonHtml(handle, sp));
    }
  }

  function promoteDeferredCover(img) {
    if (!img) return;
    var src = img.getAttribute('data-src');
    if (!src) return;
    img.removeAttribute('data-src');
    img.setAttribute('loading', 'eager');
    img.setAttribute('decoding', 'async');
    img.setAttribute('src', src);
  }

  /** Load deferred covers without flooding CDN (native lazy fails in this panel). */
  function armDeferredCovers() {
    if (!grid) return;
    if (coverSrcObserver) {
      try {
        coverSrcObserver.disconnect();
      } catch (eDisc) {
        /* ignore */
      }
      coverSrcObserver = null;
    }
    var deferred = Array.prototype.slice.call(
      grid.querySelectorAll('.wmn-search-discover__cover img[data-src]')
    );
    if (!deferred.length) return;

    // Prefer cards nearer the top of the panel.
    deferred.sort(function (a, b) {
      return a.getBoundingClientRect().top - b.getBoundingClientRect().top;
    });


    var queue = deferred.slice();
    var inflight = 0;
    var MAX_INFLIGHT = 6;
    var started = false;
    var done = false;

    function pump() {
      if (done) return;
      if (inflight >= MAX_INFLIGHT || !queue.length) {
        if (!queue.length && inflight === 0) done = true;
        return;
      }
      var img = queue.shift();
      if (!img || !img.getAttribute('data-src')) {
        pump();
        return;
      }
      inflight++;
      var onSettle = function () {
        inflight--;
        img.removeEventListener('load', onSettle);
        img.removeEventListener('error', onSettle);
        // Yield between promotions — sync cache hits used to cascade all 24.
        if (window.requestAnimationFrame) {
          window.requestAnimationFrame(pump);
        } else {
          setTimeout(pump, 16);
        }
      };
      img.addEventListener('load', onSettle);
      img.addEventListener('error', onSettle);
      promoteDeferredCover(img);
      if (img.complete) {
        onSettle();
      }
    }

    function startQueue() {
      if (started) return;
      started = true;
      pump();
    }

    // First row must finish before ANY deferred src promotion.
    // Prior 1200ms safety started the row-2 flood while row-1 was still blank
    // on cold/Slow-3G (~4.5s first-row) — that was the user-visible bottleneck.
    prioritizeFirstRowCovers();
    whenFirstRowDecoded(function () {
      if (!open || mode === 'results') return;
      if (window.requestAnimationFrame) {
        window.requestAnimationFrame(startQueue);
      } else {
        startQueue();
      }
      // Scroll near a deferred card → jump it to the front of the queue.
      if (panelEl && typeof IntersectionObserver === 'function') {
        coverSrcObserver = new IntersectionObserver(
          function (entries) {
            var i;
            for (i = 0; i < entries.length; i++) {
              if (!entries[i].isIntersecting) continue;
              var img = entries[i].target;
              if (!img.getAttribute('data-src')) {
                coverSrcObserver.unobserve(img);
                continue;
              }
              var qi = queue.indexOf(img);
              if (qi > 0) {
                queue.splice(qi, 1);
                queue.unshift(img);
              }
              coverSrcObserver.unobserve(img);
              startQueue();
            }
          },
          { root: panelEl, rootMargin: '80px 0px', threshold: 0.01 }
        );
        var di;
        for (di = 0; di < deferred.length; di++) {
          coverSrcObserver.observe(deferred[di]);
        }
      }
    }, 8000);
  }

  function existingCardMap() {
    var map = {};
    if (!grid) return map;
    // Map WRAP nodes — reorder must move the wrap (card + play), not the
    // inner <a>. Moving only the <a> orphans play buttons in empty wraps.
    var wraps = grid.querySelectorAll(
      '.wmn-search-discover__card-wrap[data-wmn-handle]'
    );
    var i;
    for (i = 0; i < wraps.length; i++) {
      var h = wraps[i].getAttribute('data-wmn-handle');
      if (h) map[h] = wraps[i];
    }
    return map;
  }

  /** Drop empty wraps left behind by older reorder bugs (play-only shells). */
  function purgeOrphanPlayWraps() {
    if (!grid) return 0;
    var kids = grid.children;
    var removed = 0;
    var i;
    for (i = kids.length - 1; i >= 0; i--) {
      var w = kids[i];
      if (
        !w ||
        !w.classList ||
        !w.classList.contains('wmn-search-discover__card-wrap')
      ) {
        // Also remove naked <a.card> nodes left by the old bug
        if (
          w &&
          w.classList &&
          w.classList.contains('wmn-search-discover__card')
        ) {
          grid.removeChild(w);
          removed++;
        }
        continue;
      }
      if (w.querySelector('a.wmn-search-discover__card')) continue;
      grid.removeChild(w);
      removed++;
    }
    return removed;
  }


  /** Detach decoded cover <img> nodes before an innerHTML wipe. */
  function salvageCoverImages() {
    var map = {};
    if (!grid) return map;
    var cards = grid.querySelectorAll(
      '.wmn-search-discover__card[data-wmn-handle]'
    );
    var i;
    for (i = 0; i < cards.length; i++) {
      var h = cards[i].getAttribute('data-wmn-handle');
      var img = cards[i].querySelector('.wmn-search-discover__cover img');
      if (h && img && (img.getAttribute('src') || img.getAttribute('data-src'))) {
        map[h] = img;
      }
    }
    return map;
  }

  /** Reattach salvaged <img> nodes so the browser keeps decoded bitmaps. */
  function restoreCoverImages(salvaged) {
    if (!grid || !salvaged) return;
    var cards = grid.querySelectorAll(
      '.wmn-search-discover__card[data-wmn-handle]'
    );
    var i;
    for (i = 0; i < cards.length; i++) {
      var h = cards[i].getAttribute('data-wmn-handle');
      var oldImg = h && salvaged[h];
      if (!oldImg) continue;
      var cover = cards[i].querySelector('.wmn-search-discover__cover');
      if (!cover) continue;
      var fresh = cover.querySelector('img');
      var oldSrc =
        oldImg.getAttribute('src') || oldImg.getAttribute('data-src');
      if (fresh) {
        var newSrc =
          fresh.getAttribute('src') || fresh.getAttribute('data-src');
        // sameImageUrl: keep decoded bitmap across _200x/_400x URL churn.
        if (oldSrc && newSrc && sameImageUrl(oldSrc, newSrc)) {
          cover.replaceChild(oldImg, fresh);
        }
      } else if (oldSrc) {
        cover.appendChild(oldImg);
      }
    }
  }

  function patchCardMeta(cardOrWrap, item) {
    if (!cardOrWrap || !item) return;
    var card = cardOrWrap.classList.contains('wmn-search-discover__card')
      ? cardOrWrap
      : cardOrWrap.querySelector('a.wmn-search-discover__card') || cardOrWrap;
    if (item.url && card.getAttribute('href') !== item.url) {
      card.setAttribute('href', item.url);
    }
    var nameEl = card.querySelector('.wmn-search-discover__name');
    if (nameEl && item.title != null && nameEl.textContent !== item.title) {
      nameEl.textContent = item.title;
    }
    var artistEl = card.querySelector('.wmn-search-discover__artist');
    if (item.artist) {
      if (artistEl) {
        if (artistEl.textContent !== item.artist) artistEl.textContent = item.artist;
      } else {
        var meta = card.querySelector('.wmn-search-discover__meta');
        if (meta) {
          var p = document.createElement('p');
          p.className = 'wmn-search-discover__artist';
          p.textContent = item.artist;
          meta.appendChild(p);
        }
      }
    } else if (artistEl && artistEl.parentNode) {
      artistEl.parentNode.removeChild(artistEl);
    }
  }

  function prefersReducedMotion() {
    try {
      return !!(
        window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
      );
    } catch (e) {
      return false;
    }
  }

  /**
   * Reorder existing card nodes instead of wiping innerHTML.
   * Keeps decoded images; FLIP-animates movers when motion is allowed.
   * Returns false when overlap is too low (caller should full-rebuild).
   */
  function reorderGridCards(list) {
    if (!grid || !list || !list.length) return false;
    purgeOrphanPlayWraps();
    var map = existingCardMap();
    var existingN = Object.keys(map).length;
    if (existingN < 2) return false;

    var overlap = 0;
    var i;
    for (i = 0; i < list.length; i++) {
      if (list[i] && list[i].handle && map[list[i].handle]) overlap++;
    }
    // Need enough shared cards for reorder to beat a full wipe.
    if (overlap < Math.max(3, Math.ceil(list.length * 0.25))) return false;

    var useFlip = !prefersReducedMotion();
    var first = {};
    var handle;
    if (useFlip) {
      for (handle in map) {
        if (!Object.prototype.hasOwnProperty.call(map, handle)) continue;
        var rect = map[handle].getBoundingClientRect();
        first[handle] = { top: rect.top, left: rect.left };
      }
    }

    var used = {};
    for (i = 0; i < list.length; i++) {
      var item = list[i];
      if (!item || !item.url) continue;
      var wrapNode = item.handle ? map[item.handle] : null;
      if (wrapNode) {
        used[item.handle] = true;
        patchCardMeta(wrapNode, item);
        grid.appendChild(wrapNode);
      } else {
        var wrap = document.createElement('div');
        // Only first row eager — appending 24 eager srcs re-floods the CDN.
        wrap.innerHTML = cardHtml(
          item,
          i < EAGER_COVER_N ? 'eager' : 'lazy'
        );
        if (wrap.firstChild) grid.appendChild(wrap.firstChild);
      }
    }

    for (handle in map) {
      if (!Object.prototype.hasOwnProperty.call(map, handle)) continue;
      if (used[handle]) continue;
      if (map[handle].parentNode === grid) {
        grid.removeChild(map[handle]);
      }
    }

    if (useFlip) {
      for (handle in used) {
        if (!Object.prototype.hasOwnProperty.call(used, handle)) continue;
        var el = map[handle];
        var from = first[handle];
        if (!el || !from) continue;
        var last = el.getBoundingClientRect();
        var dx = from.left - last.left;
        var dy = from.top - last.top;
        if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) continue;
        el.style.transition = 'none';
        el.style.transform = 'translate(' + dx + 'px,' + dy + 'px)';
        // force layout so the invert sticks before animating to identity
        void el.offsetWidth;
        el.style.transition = 'transform 160ms ease';
        el.style.transform = '';
      }
      window.setTimeout(function () {
        var nodes = grid.querySelectorAll('.wmn-search-discover__card-wrap');
        var ci;
        for (ci = 0; ci < nodes.length; ci++) {
          nodes[ci].style.transition = '';
          nodes[ci].style.transform = '';
        }
      }, 220);
    }


    return true;
  }

  /**
   * Fallback when reorder overlap math fails but SSR first-row cards exist:
   * keep every decoded card node, append only missing handles.
   */
  function expandPreservingDecoded(list) {
    if (!grid || !list || !list.length) return false;
    purgeOrphanPlayWraps();
    var map = existingCardMap();
    if (Object.keys(map).length < 4) return false;
    var used = {};
    var i;
    for (i = 0; i < list.length; i++) {
      var item = list[i];
      if (!item || !item.url) continue;
      var wrapNode = item.handle ? map[item.handle] : null;
      if (wrapNode) {
        used[item.handle] = true;
        patchCardMeta(wrapNode, item);
        grid.appendChild(wrapNode);
      } else {
        var wrap = document.createElement('div');
        wrap.innerHTML = cardHtml(
          item,
          i < EAGER_COVER_N ? 'eager' : 'lazy'
        );
        if (wrap.firstChild) grid.appendChild(wrap.firstChild);
      }
    }
    var handle;
    for (handle in map) {
      if (!Object.prototype.hasOwnProperty.call(map, handle)) continue;
      if (used[handle]) continue;
      if (map[handle].parentNode === grid) {
        grid.removeChild(map[handle]);
      }
    }
    return true;
  }

  function withResolvedImages(items) {
    var out = [];
    var needFetch = [];
    var graph = window.wmnCatalogGraph;
    for (var i = 0; i < (items || []).length; i++) {
      var item = items[i];
      if (!item || !item.url) continue;
      var img = item.image || null;
      if (
        !img &&
        item.handle &&
        graph &&
        typeof graph.getProductImage === 'function'
      ) {
        var cached = graph.getProductImage(item.handle);
        if (cached) {
          img = cached;
        } else if (
          typeof graph.hasImageCacheEntry === 'function' &&
          graph.hasImageCacheEntry(item.handle)
        ) {
          // Confirmed missing — keep the ranked slot with empty cover so
          // personalization is not replaced by unrelated bestsellers.
          out.push(item);
          continue;
        } else {
          needFetch.push(item.handle);
          // Keep ranked/personalized slots visible; hydrate patches covers later.
          out.push(item);
          continue;
        }
      } else if (!img) {
        if (item.handle) needFetch.push(item.handle);
        out.push(item);
        continue;
      }
      item.image = img;
      out.push(item);
    }
    return { items: out, needFetch: needFetch };
  }

  /** Fill incomplete rows with image-ready bestsellers (graph misses must not leave holes). */
  function backfillImageReady(list, excludeHandles, limit) {
    var out = (list || []).slice();
    var seen = {};
    var i;
    for (i = 0; i < out.length; i++) {
      if (out[i] && out[i].handle) seen[out[i].handle] = true;
    }
    for (i = 0; i < (excludeHandles || []).length; i++) {
      if (excludeHandles[i]) seen[excludeHandles[i]] = true;
    }
    var seed = loadData() || [];
    var extras = [];
    for (i = 0; i < seed.length; i++) {
      var s = seed[i];
      if (!s || !s.handle || seen[s.handle]) continue;
      extras.push(s);
    }
    var ready = withResolvedImages(extras).items;
    for (i = 0; i < ready.length && out.length < limit; i++) {
      if (!ready[i] || !ready[i].handle || seen[ready[i].handle]) continue;
      seen[ready[i].handle] = true;
      out.push(ready[i]);
    }
    return out;
  }

  function paintItems(items, nextMode, opts) {
    if (!grid) return;
    opts = opts || {};
    EAGER_COVER_N = eagerCoverBudget();
    var paintT0 = performance.now();
    var raw = items || [];
    // Empty search miss → recently viewed → For you → Best Sellers under apology.
    if (
      nextMode === 'results' &&
      !raw.length &&
      !opts.skipMissFallback &&
      !opts.missFallback
    ) {
      var bundle = missFallbackBundle();
      if (bundle && bundle.items && bundle.items.length) {
        opts = {
          missFallback: true,
          missFallbackLabel: bundle.label || '',
          forceWipe: opts.forceWipe !== false,
          skipHydrate: opts.skipHydrate
        };
        raw = bundle.items;
      }
    }
    // Any real results / miss-rail / apology paint ends the blank hold.
    if (!(opts.suppressEmpty && !raw.length)) {
      clearSuppressEmptyWatch();
    }
    var missFallback = !!opts.missFallback;
    var resolved = withResolvedImages(raw);
    var list = resolved.items;
    if (nextMode === 'bestsellers' && list.length < DISPLAY_LIMIT) {
      list = backfillImageReady(list, resolved.needFetch, DISPLAY_LIMIT);
    } else if (list.length > DISPLAY_LIMIT) {
      list = list.slice(0, DISPLAY_LIMIT);
    }
    if (nextMode === 'results' && list.length && !missFallback) {
      lastResultsForSignal = list;
      noteResultsPainted();
    }
    if (nextMode === 'bestsellers') {
      // New For you session — drop results-session trackers after clear remount.
      // (reset also happens on clear; this covers warm open)
    }
    var key = itemsSignature(list, nextMode, opts);
    if (key === lastPaintKey) {
      patchCardImages(list);
      if (resolved.needFetch.length && !opts.skipHydrate) {
        hydrateDiscoverImages(raw, resolved.needFetch);
      }
      // Re-assert apology chrome if a same-key miss rail repaint lands.
      if (missFallback) setPanelChrome(nextMode, list.length > 0, opts);
      return;
    }
    lastPaintKey = key;
    if (nextMode === 'bestsellers') {
      lastBestsellersPaint = list;
    }
    setPanelChrome(nextMode, list.length > 0, opts);
    if (!list.length) {
      grid.innerHTML = '';
      if (resolved.needFetch.length && !opts.skipHydrate) {
        hydrateDiscoverImages(raw, resolved.needFetch);
      }
      return;
    }

    // If liquid SSR already decoded the first row, never wipe/rebuild those
    // nodes — only append the rest, and delay their network until later.
    // ONLY for cold unpersonalized seed expand. Personalized / field-owned
    // For you must remount/reorder when order differs (SSR-keep would freeze).
    var existingCards = grid.querySelectorAll(
      '.wmn-search-discover__card[data-wmn-handle]'
    );
    var personalized =
      nextMode === 'bestsellers' &&
      (fieldOwnsForYou() || hasEntityPrefs() || fieldHasEnergyNodes());
    if (
      !opts.forceWipe &&
      !personalized &&
      nextMode === 'bestsellers' &&
      existingCards.length >= 8 &&
      list.length >= existingCards.length
    ) {
      var prefixOk = true;
      var pi;
      for (pi = 0; pi < 8; pi++) {
        if (
          !list[pi] ||
          existingCards[pi].getAttribute('data-wmn-handle') !== list[pi].handle
        ) {
          prefixOk = false;
          break;
        }
      }
      // Append-only: every existing card must already sit at the same index
      // in the new list. Otherwise fall through to reorder (positions 9+).
      if (prefixOk && existingCards.length > 8) {
        for (pi = 8; pi < existingCards.length; pi++) {
          if (
            !list[pi] ||
            existingCards[pi].getAttribute('data-wmn-handle') !== list[pi].handle
          ) {
            prefixOk = false;
            break;
          }
        }
      }
      if (prefixOk) {
        // Keep SSR first row; append remaining cards on rAF (or immediately
        // if first row already decoded). Do not gate behind whenFirstRowDecoded.
        prioritizeFirstRowCovers();
        lastPaintKey = key;
        lastBestsellersPaint = list;
        var appendGen = seedExpandGen;
        function appendRest() {
          if (!open || mode === 'results') return;
          if (appendGen !== seedExpandGen) return;
          var have = {};
          var existingNow = grid.querySelectorAll(
            '.wmn-search-discover__card[data-wmn-handle]'
          );
          var ei2;
          for (ei2 = 0; ei2 < existingNow.length; ei2++) {
            have[existingNow[ei2].getAttribute('data-wmn-handle')] = true;
          }
          var pi2;
          for (pi2 = 0; pi2 < list.length; pi2++) {
            if (!list[pi2] || !list[pi2].handle || have[list[pi2].handle]) {
              continue;
            }
            var wrap = document.createElement('div');
            wrap.innerHTML = cardHtml(list[pi2], 'lazy');
            if (wrap.firstChild) grid.appendChild(wrap.firstChild);
          }
          armDeferredCovers();
          if (open) resetVisibilityTracking();
        }
        if (firstRowCoversDecoded()) {
          appendRest();
        } else if (typeof requestAnimationFrame === 'function') {
          requestAnimationFrame(appendRest);
        } else {
          appendRest();
        }
        return;
      }
    }

    // Cancel any pending SSR deferred-append from an earlier paint.
    seedExpandGen += 1;

    // Prefer in-place reorder when enough cards already exist (settle paint /
    // For you reshuffles). Full wipe only when overlap is too low — still
    // salvage decoded <img> nodes so remount is not decode-bound.
    // SSR first row (8 cards): always reuse — never wipe decoded covers on open.
    var existingN = existingCards.length;
    var reordered =
      !opts.forceWipe &&
      (reorderGridCards(list) ||
        (existingN >= 8 && expandPreservingDecoded(list)));
    if (!reordered) {
      var salvaged = salvageCoverImages();
      // Prefer already-hydrated / baked URLs before building empty cover divs.
      syncItemImagesFromGraph(list);
      var html = '';
      var i;
      // Known image URL → src immediately (baked graph). data-src trickle made
      // For you wait seconds after titles already showed.
      for (i = 0; i < list.length; i++) {
        html += cardHtml(
          list[i],
          list[i] && list[i].image
            ? 'eager'
            : i < EAGER_COVER_N
              ? 'eager'
              : 'lazy'
        );
      }
      grid.innerHTML = html;
      restoreCoverImages(salvaged);
      patchGridCoversFromGraph();
      patchDiscoverPlayButtons();
    } else {
      syncItemImagesFromGraph(list);
      patchCardImages(list);
    }

    if (resolved.needFetch.length && !opts.skipHydrate) {
      hydrateDiscoverImages(raw, resolved.needFetch);
    }
    armDeferredCovers();
    if (open) resetVisibilityTracking();
  }

  function resolveBestsellersList() {
    var raw = rankedBestSellers();
    var resolved = withResolvedImages(raw);
    var list = resolved.items;
    if (list.length < DISPLAY_LIMIT) {
      list = backfillImageReady(list, resolved.needFetch, DISPLAY_LIMIT);
    } else if (list.length > DISPLAY_LIMIT) {
      list = list.slice(0, DISPLAY_LIMIT);
    }
    return { raw: raw, resolved: resolved, list: list };
  }

  function hydrateDiscoverImages(allItems, presetNeed) {
    if (
      !window.wmnCatalogGraph ||
      typeof window.wmnCatalogGraph.hydrateImages !== 'function'
    ) {
      return;
    }
    var needFetch = presetNeed ? presetNeed.slice() : [];
    if (!needFetch.length) return;
    // Remount during an in-flight hydrate used to drop the new needFetch forever
    // (lock early-return) — For you stayed blank. Queue and run after unlock.
    if (window.__wmnHydrateLock) {
      window.__wmnHydratePending = window.__wmnHydratePending || [];
      var pi;
      for (pi = 0; pi < needFetch.length; pi++) {
        if (
          needFetch[pi] &&
          window.__wmnHydratePending.indexOf(needFetch[pi]) === -1
        ) {
          window.__wmnHydratePending.push(needFetch[pi]);
        }
      }
      return;
    }
    window.__wmnHydrateLock = true;
    var hydrateT0 = Date.now();
    EAGER_COVER_N = eagerCoverBudget();
    var priority = [];
    var rest = [];
    var shown = gridShownHandles();
    var i;
    for (i = 0; i < needFetch.length; i++) {
      var h = needFetch[i];
      var idx = shown.indexOf(h);
      if (idx >= 0 && idx < EAGER_COVER_N) priority.push(h);
      else rest.push(h);
    }
    var ordered = priority.concat(rest);
    function flushPendingHydrate() {
      var pending = window.__wmnHydratePending;
      window.__wmnHydratePending = null;
      if (pending && pending.length) {
        hydrateDiscoverImages(null, pending);
      }
    }
    function applyHydratedImages() {
      try {
        if (!open || mode === 'results') return;
        if (settleRankPrimed && rankedCache && rankedCache.length) {
          syncItemImagesFromGraph(rankedCache);
          patchCardImages(rankedCache);
        } else {
          var next = resolveBestsellersList();
          patchCardImages(next.list);
        }
        patchGridCoversFromGraph();
        patchDiscoverPlayButtons();
        armDeferredCovers();
      } catch (eApply) {
        /* never abort */
      }
    }
    function queueMissingRanked() {
      if (!(settleRankPrimed && rankedCache && rankedCache.length)) return;
      var still = [];
      var si;
      for (si = 0; si < rankedCache.length; si++) {
        var rit = rankedCache[si];
        if (!rit || !rit.handle || rit.image) continue;
        if (
          window.wmnCatalogGraph &&
          typeof window.wmnCatalogGraph.hasImageCacheEntry === 'function' &&
          window.wmnCatalogGraph.hasImageCacheEntry(rit.handle)
        ) {
          continue;
        }
        still.push(rit.handle);
      }
      if (!still.length) return;
      window.__wmnHydratePending = window.__wmnHydratePending || [];
      for (si = 0; si < still.length; si++) {
        if (window.__wmnHydratePending.indexOf(still[si]) === -1) {
          window.__wmnHydratePending.push(still[si]);
        }
      }
    }
    function afterHydrate() {
      window.__wmnHydrateLock = false;
      if (open && mode === 'results') {
        var active = document.activeElement;
        if (isHeaderSearchInput(active) && inputValue(active)) {
          paintOptimistic(inputValue(active));
        }
        flushPendingHydrate();
        return;
      }
      if (open) {
        applyHydratedImages();
        queueMissingRanked();
      }
      flushPendingHydrate();
    }
    // Single batch — mid-batch patch used to throw and skip the rest.
    window.wmnCatalogGraph
      .hydrateImages(ordered)
      .then(afterHydrate)
      .catch(function () {
        window.__wmnHydrateLock = false;
        if (open) applyHydratedImages();
        flushPendingHydrate();
      });
  }

  /**
   * Instant paint — never block on hydrate/network.
   * Missing covers backfill from seed; hydrate patches in the background.
   */
  function paintPersonalizedBestsellers() {
    paintItems(rankedBestSellers(), 'bestsellers');
  }

  function renderBestSellers() {
    paintPersonalizedBestsellers();
  }

  function warmGraphThenRender() {
    var warmT0 = performance.now();
    var now = Date.now();
    var already =
      window.wmnCatalogGraph &&
      typeof window.wmnCatalogGraph.getData === 'function' &&
      !!window.wmnCatalogGraph.getData();
    // focusin + input both open discover — coalesce warm/network work only.
    // Never delay a forced typed For you paint (dirty flag or settle-primed cache).
    if (window.__wmnLastWarmMs && now - window.__wmnLastWarmMs < 400) {
      var forceTyped =
        !!window.__wmnTypedPrefsDirty ||
        (settleRankPrimed && rankedCache && rankedCache.length);
      // Typed query still in the box → keep Results / empty apology, not For you.
      if (forceTyped && !holdSearchResultsPane()) {
        try {
          window.__wmnTypedPrefsDirty = false;
        } catch (eDirty) {
          /* ignore */
        }
        remountForYouFromTypedPrefs();
      }
      return;
    }
    window.__wmnLastWarmMs = now;

    setPanelChrome('bestsellers', true);
    paintEmptyState();

    if (
      !window.wmnCatalogGraph ||
      typeof window.wmnCatalogGraph.ensureLoaded !== 'function'
    ) {
      return;
    }

    function upgradeIfStable() {
      if (!open || mode === 'results') return;
      // Rank in idle time so the first paint stays snappy.
      var run = function () {
        if (!open || mode === 'results') return;
        // Typed prefs landed while empty — apply settle cache if primed.
        if (consumeTypedPrefsDirty()) {
          seedExpandGen += 1;
          paintCachedForYou();
          return;
        }
        // Cold bestsellers with no personalization: stay put. Idle re-rank was
        // reshuffling seed ↔ affinity forever as dwell injected energy.
        // Do NOT treat fieldHasEnergyNodes alone as personalization — dwell on
        // seed bestsellers injects energy and used to wipe unfinished SSR covers.
        if (!firstRowCoversDecoded()) return;
        if (
          !settleRankPrimed &&
          !hasEntityPrefs() &&
          !fieldOwnsForYou()
        ) {
          return;
        }
        // Settle-primed order is authoritative until the next settle/invalidate.
        // Exception: lean/committed with empty field nodes means inject missed
        // the graph — re-rank now that data may be present.
        if (settleRankPrimed && rankedCache && rankedCache.length) {
          if (!(fieldOwnsForYou() && !fieldHasEnergyNodes())) {
            return;
          }
          settleRankPrimed = false;
          rankedCache = null;
          rankedCacheAt = 0;
        }
        var next = rankedBestSellers();
        var resolved = withResolvedImages(next);
        var list = resolved.items;
        if (list.length < DISPLAY_LIMIT) {
          list = backfillImageReady(list, resolved.needFetch, DISPLAY_LIMIT);
        }
        var key = itemsSignature(list, 'bestsellers');
        if (key === lastPaintKey) {
          patchCardImages(list);
          if (resolved.needFetch.length) {
            hydrateDiscoverImages(next, resolved.needFetch);
          }
          return;
        }
        // Cancel deferred seed expand — ranked paint wins.
        seedExpandGen += 1;
        paintItems(next, 'bestsellers');
      };
      if (window.requestIdleCallback) {
        window.requestIdleCallback(run, { timeout: 200 });
      } else {
        setTimeout(run, 0);
      }
    }

    if (already) {
      // Graph already in memory — still refresh if settles raced an empty field.
      if (fieldOwnsForYou() && !fieldHasEnergyNodes()) {
        refreshFieldAfterGraphLoad();
      }
      upgradeIfStable();
      return;
    }

    window.wmnCatalogGraph.ensureLoaded().then(function () {
      refreshFieldAfterGraphLoad();
      upgradeIfStable();
      patchDiscoverPlayButtons();
    });
  }

  function normalizeQuery(q) {
    if (window.wmnSearchFuzzy && typeof window.wmnSearchFuzzy.normalize === 'function') {
      return window.wmnSearchFuzzy.normalize(q);
    }
    return String(q || '')
      .toLowerCase()
      .replace(/^\s+|\s+$/g, '');
  }

  function cachePut(query, mapped) {
    var key = normalizeQuery(query);
    if (!key || !mapped) return;
    if (!resultCache[key]) {
      cacheOrder.push(key);
      while (cacheOrder.length > CACHE_MAX) {
        var old = cacheOrder.shift();
        delete resultCache[old];
      }
    }
    resultCache[key] = mapped;
  }

  function cacheLookup(query) {
    var key = normalizeQuery(query);
    if (!key) return null;
    if (resultCache[key]) {
      return { mapped: resultCache[key], exact: true, key: key };
    }
    var best = '';
    for (var i = 0; i < cacheOrder.length; i++) {
      var k = cacheOrder[i];
      if (key.indexOf(k) === 0 && k.length >= 2 && k.length > best.length) {
        best = k;
      }
    }
    if (best && resultCache[best]) {
      return { mapped: resultCache[best], exact: false, key: best };
    }
    return null;
  }

  function mergePools(primary, secondary) {
    var out = [];
    var seen = {};
    function add(list) {
      if (!list) return;
      for (var i = 0; i < list.length; i++) {
        var it = list[i];
        if (!it) continue;
        var h = it.handle || it.url;
        if (!h || seen[h]) continue;
        seen[h] = true;
        out.push(it);
      }
    }
    add(primary);
    add(secondary);
    return out;
  }

  function localFallback(query) {
    // Typed search: only graph intent candidates (genre/country/"like X"),
    // never the bestsellers pool.
    var graph = graphRankQuery(query);
    if (graph && graph.items && graph.items.length) {
      return fuzzyRank(query, graph.items, {
        skipAffinity: true,
        seedHandles: []
      });
    }
    return [];
  }

  function optimisticItems(query) {
    var cached = cacheLookup(query);
    var pool = [];
    if (cached && cached.mapped && cached.mapped.length) {
      pool = cached.mapped.slice();
    }
    var graph = graphRankQuery(query);
    if (graph && graph.items && graph.items.length) {
      pool = mergePools(graph.items, pool);
    }
    // Include local pool only as extra candidates to fuzzy-filter — never
    // return it unfiltered when the query matches nothing.
    pool = mergePools(pool, localPool());
    var ranked = fuzzyRank(query, pool, { skipAffinity: true });
    return ranked;
  }

  function clearSuppressEmptyWatch() {
    if (suppressEmptyTimer) {
      clearTimeout(suppressEmptyTimer);
      suppressEmptyTimer = null;
    }
  }

  function armSuppressEmptyWatch(seq) {
    clearSuppressEmptyWatch();
    suppressEmptySeq = seq;
    // Pop/rock OOV (Michael Jackson) used to sit on a black panel forever when
    // searchAndRender bumped requestSeq and cancelled optimistic similar, then
    // Shopify search stalled or rate-limited.
    suppressEmptyTimer = setTimeout(function () {
      suppressEmptyTimer = null;
      if (suppressEmptySeq !== requestSeq) return;
      if (!open || mode !== 'results') return;
      var hasCards =
        grid && grid.querySelector('.wmn-search-discover__card[data-wmn-handle]');
      var apologyHidden = emptyEl && emptyEl.hidden;
      if (hasCards || !apologyHidden) return;
      similarLabel = '';
      clearExplainState();
      lastPaintKey = '';
      paintItems([], 'results', { forceWipe: true });
    }, 1000);
  }

  function applyExplainFromQuery(query, graph, likeLane) {
    similarLabel = '';
    if (likeLane && likeLane.n) {
      var likePhrase = likePhraseFromQuery(query);
      var bareLike =
        !!likePhrase &&
        (!graph ||
          !graph.intent ||
          !graph.intent.entities ||
          !graph.intent.entities.length);
      if (bareLike) {
        similarLabel = likeLane.n;
        setExplainState({
          similarTo: likeLane.n,
          genres: (likeLane.g || []).slice(),
          countries: (likeLane.c || []).slice(),
          densify: false
        });
        return;
      }
    }
    if (graph && graph.intent) {
      var fromIntent = explainStateFromGraphIntent(graph.intent);
      if (fromIntent) {
        setExplainState(fromIntent);
        return;
      }
    }
    clearExplainState();
  }

  function paintOptimistic(query) {
    var ranked = optimisticItems(query);
    if (ranked.length) {
      clearSuppressEmptyWatch();
      applyExplainFromQuery(query, graphRankQuery(query), null);
      paintItems(ranked, 'results');
      return;
    }
    // Claim results mode immediately so settle/warm cannot flash personalized
    // For you over a miss while similarTo / live MB resolves. Do NOT flash
    // the apology yet — that only paints after similar/live fully fails
    // (Eric Clapton was showing "Sorry" for 1–2s before blues arrived).
    similarLabel = '';
    clearExplainState();
    paintItems([], 'results', {
      skipMissFallback: true,
      suppressEmpty: true
    });
    armSuppressEmptyWatch(requestSeq);
    paintSimilarToQuery(query, requestSeq);
  }

  function searchAndRender(query, opts) {
    opts = opts || {};
    var norm = normalizeQuery(query);
    if (!norm) return;

    var cached = cacheLookup(query);
    if (cached && cached.exact && !opts.force) {
      var cachedRanked = fuzzyRank(query, cached.mapped) || cached.mapped;
      applyExplainFromQuery(query, graphRankQuery(query), null);
      paintItems(cachedRanked, 'results');
      return;
    }

    var seq = ++requestSeq;
    if (abortCtrl && abortCtrl.abort) {
      try {
        abortCtrl.abort();
      } catch (e) {}
    }
    abortCtrl =
      typeof AbortController !== 'undefined' ? new AbortController() : null;

    // Trailing * made Shopify return the whole catalog for unknown artists
    // (e.g. Tinariwen* → 270 hits). Only wildcard very short prefix queries.
    var qParam = query;
    if (String(query || '').replace(/^\s+|\s+$/g, '').length <= 2) {
      qParam = query + '*';
    }
    var url =
      shopRoot() +
      'search?type=product&q=' +
      encodeURIComponent(qParam) +
      '&view=json';

    var fetchOpts = { credentials: 'same-origin' };
    if (abortCtrl) fetchOpts.signal = abortCtrl.signal;

    fetch(url, fetchOpts)
      .then(function (res) {
        if (!res.ok) throw new Error('search ' + res.status);
        return res.json();
      })
      .then(function (payload) {
        if (seq !== requestSeq) return;
        var raw = (payload && payload.results) || [];
        var mapped = [];
        for (var i = 0; i < raw.length; i++) {
          var m = mapSearchResult(raw[i]);
          if (m) mapped.push(m);
        }
        cachePut(query, mapped);

        // "like Tinariwen" OOV: resolve neighbour lane (affinity / live MB),
        // seed graph expand, then fall back to Similar-to path.
        var likePhrase = likePhraseFromQuery(query);
        var seeds = likePhrase
          ? seedHandlesFromItems(likePhrase, mapped)
          : [];
        var g = window.wmnCatalogGraph;
        var lanePromise = Promise.resolve(null);
        if (likePhrase && g && typeof g.resolveLikeLane === 'function') {
          lanePromise = Promise.resolve(g.ensureLoaded ? g.ensureLoaded() : null)
            .then(function () {
              return g.resolveLikeLane(likePhrase);
            })
            .catch(function () {
              return null;
            });
        } else if (
          likePhrase &&
          g &&
          typeof g.ensureAffinity === 'function'
        ) {
          lanePromise = Promise.resolve(g.ensureAffinity()).then(function () {
            return typeof g.resolveLikeLaneSync === 'function'
              ? g.resolveLikeLaneSync(likePhrase)
              : null;
          });
        }

        return lanePromise.then(function (likeLane) {
          if (seq !== requestSeq) return;
          var graph = graphRankQuery(query, {
            seedHandles: seeds,
            likeLane: likeLane || null
          });
          var pool = mapped;
          if (graph && graph.items && graph.items.length) {
            pool = mergePools(graph.items, mapped);
          }
          var ranked = fuzzyRank(query, pool, {
            seedHandles: seeds,
            likeLane: likeLane || null
          });
          if (ranked.length) {
            applyExplainFromQuery(query, graph, likeLane || null);
            paintItems(ranked, 'results');
            return;
          }
          paintSimilarToQuery(query, seq);
        });
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        if (seq !== requestSeq) return;
        paintSimilarToQuery(query, seq);
      });
  }

  /**
   * No exact catalog hit → show similar WMN artists/releases.
   * In-catalog: artists.*.g / *.r + product-emb centroid.
   * OOV: affinity map (MusicBrainz-baked) + embedding nearest neighbours.
   */
  function paintMissFallback() {
    clearSuppressEmptyWatch();
    similarLabel = '';
    clearExplainState();
    lastPaintKey = '';
    paintItems([], 'results', { forceWipe: true });
  }

  function paintSimilarItems(g, sim) {
    if (!sim || !sim.handles || !sim.handles.length) {
      paintMissFallback();
      return;
    }
    clearSuppressEmptyWatch();
    similarLabel = sim.similarTo || '';
    setExplainState(explainStateFromSimilar(sim));
    var items = [];
    var i;
    for (i = 0; i < sim.handles.length; i++) {
      var card =
        typeof g.productCard === 'function'
          ? g.productCard(sim.handles[i])
          : null;
      if (card) items.push(card);
    }
    if (!items.length) {
      paintMissFallback();
      return;
    }
    paintItems(items, 'results');
  }

  /** True when the discover input still matches the query we resolved for. */
  function queryStillActive(query) {
    try {
      var live = '';
      if (root) {
        var input =
          document.querySelector(
            '.wmn-header__search input[name="q"], .header_search_form input[name="q"], [data-dropdown="search"] input[name="q"]'
          ) || null;
        if (input) live = inputValue(input);
      }
      return normalizeQuery(live) === normalizeQuery(query);
    } catch (e) {
      return true;
    }
  }

  function paintSimilarToQuery(query, seq) {
    if (
      !window.wmnCatalogGraph ||
      typeof window.wmnCatalogGraph.ensureLoaded !== 'function'
    ) {
      paintMissFallback();
      return;
    }
    var g = window.wmnCatalogGraph;
    // "like Tinariwen" / "sounds like Bombino" → resolve artist phrase only.
    // Passing the full string misses affinity keys and poisons live MB.
    var artistQ = likePhraseFromQuery(query) || query;

    function stale() {
      // Same typed query: allow miss paint even if searchAndRender bumped seq
      // (that bump used to cancel optimistic similar and leave a black panel).
      if (seq == null) return false;
      if (seq === requestSeq) return false;
      return !queryStillActive(query);
    }

    Promise.resolve(g.ensureLoaded())
      .then(function () {
        if (typeof g.ensureAffinity === 'function') return g.ensureAffinity();
        return null;
      })
      .then(function () {
        if (stale()) return { done: true };
        if (typeof g.similarToQuery !== 'function') {
          paintMissFallback();
          return { done: true };
        }
        var sim = g.similarToQuery(artistQ, { limit: GRID_LIMIT });
        if (sim && sim.handles && sim.handles.length) {
          paintSimilarItems(g, sim);
          return { done: true };
        }
        // Baked miss → live MusicBrainz lane (specific WMN genres only).
        if (typeof g.fetchLiveAffinity !== 'function') {
          paintMissFallback();
          return { done: true };
        }
        return g.fetchLiveAffinity(artistQ).then(function (row) {
          return { done: false, liveRow: row };
        });
      })
      .then(function (state) {
        if (!state || state.done) return;
        if (stale()) return;
        var simLive = g.similarToQuery(artistQ, {
          limit: GRID_LIMIT,
          liveRow: state.liveRow
        });
        paintSimilarItems(g, simLive);
      })
      .catch(function () {
        if (stale()) return;
        paintMissFallback();
      });
  }

  function syncFromInput(input) {
    var q = inputValue(input);
    if (!q) {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
        debounceTimer = null;
      }
      var toFlush = pendingSearchSignalQuery || lastSearchSignal;
      if (searchSignalTimer) {
        clearTimeout(searchSignalTimer);
        searchSignalTimer = null;
      }
      var bounce = mode === 'results' && isBounceClear();
      if (bounce) {
        // Noise gate: open → clear <800ms, no dwell/click — do not inject.
        pendingSearchSignalQuery = '';
        lastSearchSignal = '';
        if (
          window.wmnIntentField &&
          typeof window.wmnIntentField.onBounceClear === 'function'
        ) {
          try {
            window.wmnIntentField.onBounceClear(toFlush || '');
          } catch (eB) {
            /* ignore */
          }
        }
        if (
          window.wmnIntentAnalytics &&
          typeof window.wmnIntentAnalytics.log === 'function'
        ) {
          try {
            window.wmnIntentAnalytics.log({
              type: 'bounce_clear',
              q: toFlush || null,
              mode: mode,
              meta: {
                elapsedMs: resultsShownAt ? Date.now() - resultsShownAt : null
              }
            });
          } catch (eLog) {
            /* ignore */
          }
        }
        try {
          window.__wmnTypedPrefsDirty = false;
        } catch (eD) {
          /* ignore */
        }
        resetResultsSession();
        requestSeq++;
        // Bounce clears the typed inject, but if the field still owns For you
        // from earlier settles, do not fall back to seed bestsellers.
        if (
          (settleRankPrimed && rankedCache && rankedCache.length) ||
          fieldOwnsForYou() ||
          fieldHasEnergyNodes() ||
          hasEntityPrefs()
        ) {
          paintForYouNow('bounce-clear');
          resetVisibilityTracking();
        } else {
          paintEmptyState();
        }
        return;
      }
      if (toFlush) commitSearchSignal(toFlush);
      pendingSearchSignalQuery = '';
      // Allow the same genre to re-signal after clear → type again.
      lastSearchSignal = '';
      resetResultsSession();
      requestSeq++;
      var dirty = consumeTypedPrefsDirty();
      var fieldOwns = fieldOwnsForYou() || fieldHasEnergyNodes();
      // Empty input: nuclear For you when settle/prefs/field, else empty seed.
      if (
        dirty ||
        (settleRankPrimed && rankedCache && rankedCache.length) ||
        fieldOwns ||
        hasEntityPrefs()
      ) {
        paintForYouNow('clear');
        logClearRemount();
        resetVisibilityTracking();
      } else {
        paintEmptyState();
      }
      return;
    }

    // Debounced entity bump from typed query (search as signal)
    scheduleSearchSignal(q);

    // Instant local/cached paint — no debounce.
    paintOptimistic(q);

    var cached = cacheLookup(q);
    if (cached && cached.exact) {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
        debounceTimer = null;
      }
      return;
    }

    if (debounceTimer) clearTimeout(debounceTimer);
    // Keep network search tight — optimistic local paint already filled the grid.
    var wait = q.length <= 2 ? 0 : DEBOUNCE_MS;
    debounceTimer = setTimeout(function () {
      debounceTimer = null;
      searchAndRender(q);
    }, wait);
  }

  function headerBottom() {
    var sticky = qs('.sticky_nav.sticky_nav--stick');
    var bottom = 0;
    if (sticky) {
      var sr = sticky.getBoundingClientRect();
      if (sr.height > 40 && sr.top > -8 && sr.top < 12 && sr.bottom > 40) {
        bottom = Math.max(sr.bottom, 48);
      }
    }
    if (!bottom) {
      var bar =
        qs('.wmn-header-shell .wmn-header__bar') ||
        qs('.wmn-header-shell .header') ||
        qs('.wmn-header-shell') ||
        qs('#header');
      if (!bar) return 56;
      var rect = bar.getBoundingClientRect();
      if (rect.bottom <= 0 || rect.top > 80) return 56;
      bottom = Math.max(rect.bottom, 48);
    }

    var promo =
      qs('.wmn-header-shell .promo_banner') ||
      qs('#header .promo_banner') ||
      qs('.promo_banner');
    if (promo) {
      var ps = window.getComputedStyle(promo);
      if (ps.display !== 'none' && ps.visibility !== 'hidden') {
        var pr = promo.getBoundingClientRect();
        if (pr.height > 0) {
          bottom = Math.max(bottom, pr.bottom);
          var shell = qs('.wmn-header-shell .header') || qs('.wmn-header-shell');
          if (shell) {
            bottom = Math.max(bottom, shell.getBoundingClientRect().bottom);
          }
        }
      }
    }
    var topBar = qs('.wmn-header-shell .top_bar');
    if (topBar) {
      var ts = window.getComputedStyle(topBar);
      if (ts.display !== 'none' && ts.visibility !== 'hidden') {
        var tr = topBar.getBoundingClientRect();
        if (tr.height > 0) bottom = Math.max(bottom, tr.bottom);
      }
    }

    return Math.round(Math.max(bottom, 48));
  }

  function setTopOffset() {
    if (!root) return;
    document.documentElement.style.setProperty(
      '--wmn-discover-top',
      headerBottom() + 'px'
    );
  }

  function hideClassicAutocomplete() {
    qsa('.search__results-wrapper, .search__results').forEach(function (el) {
      el.style.display = 'none';
    });
  }

  function scheduleWarm(input, showT0) {
    pendingShowInput = input || pendingShowInput;
    if (warmScheduled) {
      return;
    }
    warmScheduled = true;
    var t0 = showT0 || performance.now();
    var runWarm = function () {
      warmScheduled = false;
      if (!open) return;
      var inp = pendingShowInput;
      var q = inp ? inputValue(inp) : '';
      if (inp) {
        if (!q) warmGraphThenRender();
        else syncFromInput(inp);
      } else {
        warmGraphThenRender();
      }
      requestAnimationFrame(function () {
        setTopOffset();
        resetVisibilityTracking();
      });
    };
    if (typeof window.requestAnimationFrame === 'function') {
      requestAnimationFrame(function () {
        requestAnimationFrame(runWarm);
      });
    } else {
      setTimeout(runWarm, 0);
    }
  }

  function show(input) {
    if (!root) return;
    var showT0 = performance.now();
    var early = false;
    try {
      early = !!window.__wmnDiscoverEarlyOpen;
    } catch (eEarly) {
      early = false;
    }
    var chromeAlready =
      open ||
      early ||
      (!root.hidden && root.classList.contains('is-open'));

    // Early head-open already flipped is-open. Do NOT re-run setTopOffset /
    // setPanelChrome layout work in the same turn — that delayed first paint
    // ~200–300ms under CPU×4/6 after early open (H-C).
    if (!chromeAlready) {
      if (input && inputValue(input)) {
        setPanelChrome('results', true);
      } else {
        setPanelChrome('bestsellers', true);
      }
      root.hidden = false;
      root.classList.add('is-open');
      root.setAttribute('aria-hidden', 'false');
      document.body.classList.add('wmn-discover-open');
      hideClassicAutocomplete();
    } else {
      // Adopt early chrome; light class sync only (no layout reads).
      root.hidden = false;
      root.classList.add('is-open');
      root.setAttribute('aria-hidden', 'false');
      document.body.classList.add('wmn-discover-open');
      if (input && inputValue(input) && mode !== 'results') {
        setPanelChrome('results', true);
      }
    }
    open = true;
    if (!openedAt) openedAt = Date.now();
    suppressNextOutsideClose = false;
    // Claim bandwidth for SSR covers immediately — don't wait for warm rAF.
    prioritizeFirstRowCovers();

    // Yield so the browser can paint chrome BEFORE ranking / grid HTML.
    scheduleWarm(input, showT0);
  }

  function hide() {
    if (!root) return;
    if (coverSrcObserver) {
      try {
        coverSrcObserver.disconnect();
      } catch (eObs) {
        /* ignore */
      }
      coverSrcObserver = null;
    }
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
    requestSeq++;
    if (abortCtrl && abortCtrl.abort) {
      try {
        abortCtrl.abort();
      } catch (e) {}
    }
    clearHoverTimer();
    if (visibilityObserver) {
      try {
        visibilityObserver.disconnect();
      } catch (e2) {
        /* ignore */
      }
      visibilityObserver = null;
    }
    visibleLogged = {};
    resetResultsSession();
    root.hidden = true;
    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    try {
      root.style.display = '';
    } catch (eDisp) {
      /* ignore */
    }
    document.body.classList.remove('wmn-discover-open');
    open = false;
    warmScheduled = false;
    pendingShowInput = null;
    lastPointerShowAt = 0;
    try {
      window.__wmnDiscoverEarlyOpen = false;
    } catch (eEarly) {
      /* ignore */
    }
  }

  function inputValue(input) {
    return (input && input.value ? input.value : '').replace(/^\s+|\s+$/g, '');
  }

  function isHeaderSearchInput(input) {
    return !!(
      input &&
      input.name === 'q' &&
      input.closest &&
      input.closest(
        '.wmn-header__search, .header_search_form, [data-dropdown="search"]'
      )
    );
  }

  function isSearchChrome(el) {
    return !!(el && el.closest && el.closest(SEARCH_SAFE));
  }

  function isDiscoverPanel(el) {
    return !!(
      el &&
      el.closest &&
      el.closest('#wmn-search-discover .wmn-search-discover__panel')
    );
  }

  function syncQueryClass(input) {
    if (!input || !input.closest) return;
    var form = input.closest('form');
    if (!form) return;
    if (inputValue(input).length > 0) form.classList.add('wmn-has-query');
    else form.classList.remove('wmn-has-query');
  }

  function clearAndClose(fromBtn) {
    if (withinIgnoreWindow()) return;
    var form = fromBtn && fromBtn.closest ? fromBtn.closest('form') : null;
    var input = form
      ? form.querySelector('input[name="q"]')
      : document.activeElement;
    var hadQuery = !!(input && input.name === 'q' && inputValue(input).length);
    if (hadQuery) {
      // Commit typed settle once, then remount For you once.
      // Do NOT dispatch input — that used to fire a second nuclear wipe mid-hydrate
      // and leave empty cover divs even after imageCache was full.
      var toFlush = pendingSearchSignalQuery || lastSearchSignal;
      if (toFlush) {
        flushSearchSignal(toFlush);
      }
      pendingSearchSignalQuery = '';
      lastSearchSignal = '';
      resetResultsSession();
      requestSeq++;
      if (input && input.name === 'q') {
        input.value = '';
        syncQueryClass(input);
      }
      paintForYouNow('clear-btn');
      logClearRemount();
      resetVisibilityTracking();
      if (input && input.focus) {
        try {
          /* preventScroll: clear must not yank homepage scroll position */
          if (typeof input.focus.call === 'function') {
            input.focus({ preventScroll: true });
          } else {
            input.focus();
          }
        } catch (eF) {
          try {
            input.focus();
          } catch (eF2) {
            /* ignore */
          }
        }
      }
      return;
    }
    if (input && input.name === 'q') {
      input.value = '';
      syncQueryClass(input);
      try {
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      } catch (err) {}
    }
    hide();
    if (input && input.blur) input.blur();
  }

  function onFocus(e) {
    var input = e.target;
    if (!isHeaderSearchInput(input)) return;
    // pointerdown already opened chrome — skip duplicate show on same gesture.
    if (open && Date.now() - lastPointerShowAt < 400) {
      syncQueryClass(input);
      scheduleWarm(input, performance.now());
      return;
    }
    syncQueryClass(input);
    show(input);
  }

  function onFocusOut(e) {
    var input = e.target;
    if (!isHeaderSearchInput(input)) return;
    // Flush pending typed signal; do not remount For you while query still has text.
    var toFlush = pendingSearchSignalQuery || inputValue(input);
    if (toFlush) flushSearchSignal(toFlush);
  }

  function onInput(e) {
    var input = e.target;
    if (!isHeaderSearchInput(input)) return;
    syncQueryClass(input);
    if (!open) show(input);
    else {
      hideClassicAutocomplete();
      syncFromInput(input);
    }
  }

  function onSearchPointerDown(e) {
    // Ignore legacy mousedown duplicates if any remain bound.
    if (e && e.type === 'mousedown') return;
    var el = eventEl(e.target);
    if (!isSearchChrome(el)) return;
    suppressNextOutsideClose = true;

    if (el.closest && el.closest('[data-wmn-search-clear]')) return;

    var input = null;
    if (el.name === 'q') {
      input = el;
    } else if (el.closest) {
      var form = el.closest('form');
      if (form) input = form.querySelector('input[name="q"]');
    }
    // Mobile icon opens dropdown then focuses input — still open discover chrome.
    if (!input && el.closest && el.closest('.icon-search, a[data-dropdown-rel="search"]')) {
      input =
        qs('.wmn-header__search input[name="q"]') ||
        qs('[data-dropdown="search"] input[name="q"]') ||
        qs('.header_search_form input[name="q"]');
    }
    if (!input || !isHeaderSearchInput(input)) {
      // Icon-only path: open chrome without a resolved input.
      if (el.closest && el.closest('.icon-search, a[data-dropdown-rel="search"]')) {
        lastPointerShowAt = Date.now();
        show(null);
      }
      return;
    }

    syncQueryClass(input);
    lastPointerShowAt = Date.now();
    show(input);
  }

  function onClearClick(e) {
    var el = eventEl(e.target);
    if (!el || !el.closest) return;
    var btn = el.closest('[data-wmn-search-clear]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    clearAndClose(btn);
  }

  function tryOutsideClose(e) {
    if (!open) return;
    if (suppressNextOutsideClose) {
      suppressNextOutsideClose = false;
      return;
    }
    if (withinIgnoreWindow()) return;

    var t = eventEl(e.target);
    if (!t) return;
    if (t.closest && t.closest('[data-wmn-discover-close]')) {
      hide();
      return;
    }
    if (isDiscoverPanel(t)) return;
    if (isSearchChrome(t)) return;
    hide();
  }

  function onKey(e) {
    if (e.key === 'Escape' && open) {
      hide();
      var active = document.activeElement;
      if (active && active.blur) active.blur();
      return;
    }
    // Enter: flush settle immediately (don't wait out SEARCH_SIGNAL_MS).
    // Exact genre queries → tag collection (avoids Shopify body/semantic false hits).
    if (e.key === 'Enter') {
      var input = e.target;
      if (!isHeaderSearchInput(input)) {
        input = document.activeElement;
      }
      if (!isHeaderSearchInput(input)) return;
      var q = pendingSearchSignalQuery || inputValue(input);
      if (q && normalizeQuery(q).length >= 2) {
        flushSearchSignal(q);
      }
      var genreSlug = exactGenreSlug(q);
      if (genreSlug) {
        var dest = genreCollectionUrl(genreSlug);
        if (dest) {
          e.preventDefault();
          window.location.href = dest;
        }
      }
    }
  }

  function clearHoverTimer() {
    if (hoverTimer) {
      clearTimeout(hoverTimer);
      hoverTimer = null;
    }
    hoverHandle = null;
    hoverStartedAt = 0;
  }

  function resetVisibilityTracking() {
    visibleLogged = {};
    cardSeenAt = {};
    if (visibilityObserver) {
      try {
        visibilityObserver.disconnect();
      } catch (e) {
        /* ignore */
      }
      visibilityObserver = null;
    }
    if (!open || !grid || typeof window.IntersectionObserver !== 'function') {
      return;
    }
    var timers = {};
    visibilityObserver = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var entry = entries[i];
          var card = entry.target;
          var handle = card.getAttribute('data-wmn-handle');
          if (!handle) continue;
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            if (!cardSeenAt[handle]) cardSeenAt[handle] = Date.now();
            if (visibleLogged[handle] || timers[handle]) continue;
            timers[handle] = setTimeout(function (h, el) {
              return function () {
                timers[h] = null;
                if (!open || visibleLogged[h]) return;
                var rect = el.getBoundingClientRect();
                if (rect.height < 8 || rect.bottom < 0 || rect.top > window.innerHeight) {
                  return;
                }
                visibleLogged[h] = true;
                markResultsEngaged(h);
                if (
                  window.wmnIntentAnalytics &&
                  typeof window.wmnIntentAnalytics.log === 'function'
                ) {
                  var artistEl = el.querySelector('.wmn-search-discover__artist');
                  try {
                    window.wmnIntentAnalytics.log({
                      type: 'visible',
                      handle: h,
                      dwellMs: 1500,
                      artist: artistEl
                        ? (artistEl.textContent || '').trim()
                        : null,
                      mode: mode
                    });
                  } catch (err) {
                    /* ignore */
                  }
                }
                if (
                  window.wmnIntentField &&
                  typeof window.wmnIntentField.onVisible === 'function'
                ) {
                  try {
                    window.wmnIntentField.onVisible(h, 1500);
                    if (window.wmnIntentField.learnFromSignal) {
                      window.wmnIntentField.learnFromSignal('visible', true, {
                        handle: h
                      });
                    }
                  } catch (errF) {
                    /* ignore */
                  }
                }
              };
            }(handle, card), 1500);
          } else {
            // Left viewport — scroll-past if briefly seen
            if (timers[handle]) {
              clearTimeout(timers[handle]);
              timers[handle] = null;
            }
            if (cardSeenAt[handle] && !cardEngaged[handle] && !scrollPastDone[handle]) {
              var dur = Date.now() - cardSeenAt[handle];
              delete cardSeenAt[handle];
              if (dur > 0 && dur < SCROLL_PAST_MS) {
                scrollPastDone[handle] = true;
                emitSkip(handle, 'scroll_past', dur);
              }
            } else if (cardSeenAt[handle]) {
              delete cardSeenAt[handle];
            }
          }
        }
      },
      { threshold: [0.5] }
    );
    var cards = grid.querySelectorAll('.wmn-search-discover__card[data-wmn-handle]');
    for (var c = 0; c < cards.length; c++) {
      visibilityObserver.observe(cards[c]);
    }
  }

  function onDiscoverHover(e) {
    if (!open || !root) return;
    var el = eventEl(e.target);
    if (!el || !el.closest) return;
    var card = el.closest('.wmn-search-discover__card');
    var related =
      e.relatedTarget && e.relatedTarget.closest
        ? e.relatedTarget.closest('.wmn-search-discover__card')
        : null;

    if (e.type === 'pointerover') {
      if (!card || !root.contains(card)) return;
      if (related === card) return;
      var handle = card.getAttribute('data-wmn-handle');
      if (!handle) return;
      if (hoverHandle === handle && hoverTimer) return;
      clearHoverTimer();
      hoverHandle = handle;
      hoverStartedAt = Date.now();
      hoverTimer = setTimeout(function () {
        var dwell = Date.now() - hoverStartedAt;
        hoverTimer = null;
        if (dwell < 500) return;
        markResultsEngaged(handle);
        var artistEl = card.querySelector('.wmn-search-discover__artist');
        if (
          window.wmnIntentAnalytics &&
          typeof window.wmnIntentAnalytics.log === 'function'
        ) {
          try {
            window.wmnIntentAnalytics.log({
              type: 'hover',
              handle: handle,
              dwellMs: dwell,
              artist: artistEl ? (artistEl.textContent || '').trim() : null,
              mode: mode
            });
          } catch (err) {
            /* ignore */
          }
        }
        if (
          window.wmnIntentField &&
          typeof window.wmnIntentField.onHover === 'function'
        ) {
          try {
            window.wmnIntentField.onHover(handle, dwell);
            if (window.wmnIntentField.learnFromSignal) {
              window.wmnIntentField.learnFromSignal('hover', true, {
                handle: handle
              });
            }
          } catch (errF) {
            /* ignore */
          }
        }
      }, 500);
      return;
    }

    if (e.type === 'pointerout' || e.type === 'pointercancel') {
      if (!card || !root.contains(card)) {
        clearHoverTimer();
        return;
      }
      if (related === card) return;
      if (hoverHandle === card.getAttribute('data-wmn-handle')) {
        clearHoverTimer();
      }
    }
  }

  function onDiscoverCardClick(e) {
    var el = eventEl(e.target);
    if (!el || !el.closest) return;
    var card = el.closest('.wmn-search-discover__card');
    if (!card) return;
    if (root && !root.contains(card)) return;
    var handle = card.getAttribute('data-wmn-handle') || handleFromUrl(card.getAttribute('href'));
    if (!handle) return;
    markResultsEngaged(handle);
    var nameEl = card.querySelector('.wmn-search-discover__name');
    var artistEl = card.querySelector('.wmn-search-discover__artist');
    // Prefer the live input (what they see typed), then last settled query.
    var qLive = '';
    try {
      var inp =
        document.querySelector(
          '.wmn-header__search input[name="q"], .header_search_form input[name="q"], [data-dropdown="search"] input[name="q"]'
        ) || null;
      qLive = normalizeQuery(inputValue(inp));
    } catch (eQ) {
      qLive = '';
    }
    var qClick = qLive || lastFlushedQuery || normalizeQuery(activeDiscoverQuery());
    if (
      window.wmnEngagement &&
      typeof window.wmnEngagement.recordSearchClick === 'function'
    ) {
      try {
        window.wmnEngagement.recordSearchClick({
          handle: handle,
          title: nameEl ? (nameEl.textContent || '').trim() : '',
          artist: artistEl ? (artistEl.textContent || '').trim() : '',
          kind: 'search_click',
          q: qClick
        });
      } catch (err) {
        /* ignore */
      }
    }
  }

  function bind() {
    if (bound) return;
    bound = true;
    document.addEventListener('focusin', onFocus, true);
    document.addEventListener('focusout', onFocusOut, true);
    document.addEventListener('input', onInput, true);
    document.addEventListener('keyup', onInput, true);
    document.addEventListener('click', onClearClick, true);
    document.addEventListener('click', onDiscoverCardClick, true);
    document.addEventListener('pointerdown', onSearchPointerDown, true);
    document.addEventListener('pointerdown', tryOutsideClose, false);
    document.addEventListener('keydown', onKey, true);
    // Search form submit (button / enter) — same exact-genre collection redirect.
    document.addEventListener(
      'submit',
      function (e) {
        var form = e.target;
        if (!form || !form.querySelector) return;
        var input =
          form.querySelector(
            '.wmn-header__search input[name="q"], .header_search_form input[name="q"], input[name="q"]'
          ) || null;
        if (!input || !isHeaderSearchInput(input)) return;
        var q = inputValue(input);
        var genreSlug = exactGenreSlug(q);
        if (!genreSlug) return;
        var dest = genreCollectionUrl(genreSlug);
        if (!dest) return;
        e.preventDefault();
        window.location.href = dest;
      },
      true
    );
    if (root) {
      root.addEventListener('pointerover', onDiscoverHover, true);
      root.addEventListener('pointerout', onDiscoverHover, true);
      root.addEventListener('pointercancel', onDiscoverHover, true);
    }
    window.addEventListener(
      'resize',
      function () {
        if (open) setTopOffset();
      },
      { passive: true }
    );
    window.addEventListener(
      'scroll',
      function () {
        if (open) setTopOffset();
      },
      { passive: true }
    );
    document.addEventListener('wmn:preferences-updated', function () {
      invalidateRankedCache();
      // Do NOT remount the open grid from dwell/prefs updates.
      // Visible cards → learn → remount → new visibles was an infinite thrash.
      // Intentional remounts: search settle, clear, warm open, and rail-affinity.
    });
    document.addEventListener('wmn:intent-field-updated', function () {
      invalidateRankedCache();
      // Learning from visible/hover on discover cards must not reshuffle the open panel.
    });
    // Rough Guides (and other) rails: live remount For you — safe because
    // discover visibility observers are not the signal source (no thrash loop).
    var railAffinityTimer = 0;
    document.addEventListener('wmn:rail-affinity', function () {
      invalidateRankedCache();
      if (!open) return;
      var q = '';
      try {
        var active = document.activeElement;
        if (isHeaderSearchInput(active)) q = inputValue(active);
        if (!q) {
          var inp = document.querySelector(
            '.wmn-header__search input[name="q"], .header_search_form input[name="q"]'
          );
          q = inputValue(inp);
        }
      } catch (eQ) {
        q = '';
      }
      if (q) return; // only reshuffle empty-query For you / Best Sellers
      if (railAffinityTimer) clearTimeout(railAffinityTimer);
      railAffinityTimer = setTimeout(function () {
        railAffinityTimer = 0;
        if (!open) return;
        var q2 = '';
        try {
          var active2 = document.activeElement;
          if (isHeaderSearchInput(active2)) q2 = inputValue(active2);
          if (!q2) {
            var inp2 = document.querySelector(
              '.wmn-header__search input[name="q"], .header_search_form input[name="q"]'
            );
            q2 = inputValue(inp2);
          }
        } catch (eQ2) {
          q2 = '';
        }
        if (q2) return;
        function paint() {
          settleRankPrimed = false;
          rankedCache = null;
          rankedCacheAt = 0;
          paintCachedForYouNuclear('rail-affinity');
          resetVisibilityTracking();
        }
        try {
          if (
            window.wmnCatalogGraph &&
            typeof window.wmnCatalogGraph.ensureLoaded === 'function'
          ) {
            window.wmnCatalogGraph.ensureLoaded().then(paint).catch(paint);
            return;
          }
        } catch (eLoad) {
          /* fall through */
        }
        paint();
      }, 450);
    });
  }

  function boot() {
    root = document.getElementById('wmn-search-discover');
    if (!root) return;
    grid = qs('[data-wmn-discover-grid]', root);
    emptyEl = qs('[data-wmn-discover-empty]', root);
    titleEl = qs('[data-wmn-discover-title]', root) || qs('.wmn-search-discover__title', root);
    whyEl = qs('[data-wmn-discover-why]', root);
    panelEl = qs('.wmn-search-discover__panel', root);
    bind();
    // Prefetch sales-ranked Best Sellers so cold open matches all-time order
    warmBestSellersSalesOrder();
    var active = document.activeElement;
    var early = false;
    try {
      early = !!window.__wmnDiscoverEarlyOpen;
    } catch (eEarly) {
      early = false;
    }
    if (isHeaderSearchInput(active)) {
      show(active);
    } else if (early && root.classList.contains('is-open')) {
      // Early chrome opened before this script parsed — fill grid now.
      open = true;
      openedAt = Date.now();
      var input =
        qs('.wmn-header__search input[name="q"]') ||
        qs('.header_search_form input[name="q"]');
      show(input || null);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function () {
    bound = false;
    data = null;
    try {
      window.__wmnBsSalesWarm = null;
    } catch (eReset) {
      /* ignore */
    }
    boot();
  });

  if (window.InstantClick) {
    try {
      InstantClick.on('change', function () {
        hide();
        bound = false;
        data = null;
        try {
          window.__wmnBsSalesWarm = null;
        } catch (eIc) {
          /* ignore */
        }
        setTimeout(boot, 0);
      });
    } catch (err) {}
  }
})();
